import { initAnalytics, trackEvent, trackPageView } from "./analytics.js";
import {
  ENGAGEMENT_EVENTS,
  reportDomainForScreen,
} from "./analytics-contract.js";
import {
  POSTAL_CODE_ERROR,
  sanitizeReportPostalCodeEdit,
  validateArizonaPostalCode,
} from "./arizona-postal-code.js";
import {
  ANOTHER_PERSON_RESUME_PATH,
  assignAccountReportSubmissionIds,
  authenticatedSubmissionIsComplete,
  clearAccountReportDraft,
  clearAccountReportSubmissionIds,
  isAuthenticatedAnotherPersonSubmission,
  loadAccountReportDraftForAuthenticatedResume,
  prepareAccountReportRegistration,
  prepareAccountReportDraftForAppStart,
  resumeAccountReportRegistration,
} from "./account-report-handoff.js";
import {
  ACCOUNT_SESSION_TIMEOUT_MS,
  accountSessionDetails,
  createAccountSessionRequestGuard,
  initializeReportingBeforeAccountCheck,
  reconcileAccountSessionFlow,
  selfReportPostalCodeForAccountSession,
  shouldUseAccountPostalCode as shouldUseSessionPostalCode,
} from "./account-session.js";
import communityPulse from "./community-pulse.js";
import {
  COMMUNITY_REPORT_FORMATTER_I18N,
  interpolateCommunityReportCopy,
} from "./community-report-copy.js";
import {
  initializeSiteSettingsMenu,
  storedSiteLanguage,
  storedSiteTheme,
} from "./site-settings.js";
import { readPhotoGpsLocation } from "./exif-location.js";
import { cleanupPhotoLocationMap, initializePhotoLocationMap } from "./photo-location-map.js";
import {
  createLocationAutocompleteSearch,
  emptyLocationAutocompleteState,
  recentTravelLocationIsComplete,
} from "./location-autocomplete.js";
import {
  REPORT_LOCATION_SOURCE,
  REPORT_LOCATION_STATUS,
  applyReportLocationSearchProgress,
  beginReportLocationSearch,
  canAutoDiscoverReportLocation,
  confirmedReportCoordinates,
  confirmReportLocation,
  createReportLocationState,
  isValidCoordinatePair,
  initializeReportLocationPin,
  isConfirmedPhotoReportLocation,
  moveReportLocationMarker,
  orderedReportPhotoSources,
  reportLocationDecisionResolved,
  reportLocationMapFailed,
  reportLocationZipChanged,
  reportLocationSourceRemoved,
  setReportLocationBrowserCoordinates,
  shouldIncludeReportLocationStepForState,
} from "./photo-location-state.js";
import { buildCommunityReportPayload as buildPayloadFromState } from "./payload.js";
import {
  applyDetectedHumanLocation,
  applyManualHumanZip,
  canApplyDetectedHumanLocation,
} from "./human-location-state.js";
import { locationH3FromCoordinates } from "./location-h3.js";
import {
  featureContainsCoordinate,
  initialReportLocationCoordinates,
  reportLocationHasValidStartingPoint,
} from "./report-location-start.js";
import {
  ANIMAL_GROUP_OPTIONS,
  ANIMAL_REPORTER_FEELING_OPTIONS,
  activeAnimalCountField,
  animalSpeciesOptions,
  animalStatusOptions,
  animalTypeForSpecies,
  isAnimalFoundDateInRange,
} from "./animal-report-flow.js";
import {
  ANIMAL_COUNT_MAX_LENGTH,
  PEOPLE_SICK_COUNT_MAX_LENGTH,
  isPositiveIntegerText,
  sanitizeCountInput,
} from "./count-input.js";
import {
  ENVIRONMENT_ISSUE_OPTIONS,
  ENVIRONMENT_REPORTER_FEELING_OPTIONS,
  ENVIRONMENTAL_CONCERN_OPTIONS,
  INSECT_VECTOR_OPTIONS,
  isEnvironmentObservedDateInRange,
  resetEnvironmentReportState,
} from "./environment-report-flow.js";
import { arizonaIsoDate } from "./arizona-date.js";
import {
  ACCOUNT_REGISTRATION_URL,
  POST_SUBMIT_REGISTRATION_STEP,
  postSubmitStepForPayloads,
  routeStepForAccountSession,
} from "./post-submit-registration.js";
export { postSubmitStepForPayloads };
import {
  defaultReportMapTimeWindow,
  formatReportMapDate,
  sliderDateForIndex,
  sliderIndexForDate,
  sliderWindowDays,
} from "./map-time-window.js";
import { weeklyCheckinCategoryFromSearch } from "./weekly-checkin-handoff.js";
import {
  classifySubmissionResult,
  queuePendingReports,
  removePendingReports,
} from "./submission-outcome.js";
import { MAPBOX_REVERSE_GEOCODE_URL } from "./src/map/constants.js";
import { loadArizonaZipGeometry } from "./src/map/geo.js";
import { mapLabLayerToggleMarkup, mapLabSymptomFilterMarkup, reportDotLegendMarkup, setMapLabCategoryVisible, setMapLabSymptomMenuOpen, setMapLabSymptoms, toggleMapLabSymptom, toggleMapLabSymptomMenu } from "./src/map/controls.js";
import { loadCommunityReportCount, recomputeMapLabDerived, reportMapTimePeriodText } from "./src/map/data.js";
import { mapboxToken, renderReportMaps, updateReportMapSources } from "./src/map/index.js";
import { searchZipOnMap } from "./src/map/zip-search.js";
import { setMapRuntime } from "./src/map/runtime.js";
import { symptomLabel } from "./symptom-labels.js";
import Navigo from "navigo";

export const SYMPTOMS = [
  "Cough/congestion",
  "Nausea",
  "Difficulty breathing",
  "Sore throat",
  "Rash",
  "Fever",
  "Chills",
  "Diarrhea",
  "Bleeding from body openings",
  "Red eyes",
  "Body aches",
  "Discolored or bloody urine",
  "Loss of smell or taste",
  "Yellow skin/eyes",
  "Other",
];

export const SYMPTOM_BADGES = Object.freeze({
  "Cough/congestion": "./assets/symptom-badges/cough-congestion.png",
  Nausea: "./assets/symptom-badges/nausea-vomiting.png",
  "Difficulty breathing": "./assets/symptom-badges/difficulty-breathing.png",
  "Sore throat": "./assets/symptom-badges/sore-throat.png",
  Rash: "./assets/symptom-badges/rash.png",
  Fever: "./assets/symptom-badges/fever.png",
  Chills: "./assets/symptom-badges/chills.png",
  Diarrhea: "./assets/symptom-badges/diarrhea.png",
  "Bleeding from body openings": "./assets/symptom-badges/bleeding.png",
  "Red eyes": "./assets/symptom-badges/red-eyes.png",
  "Body aches": "./assets/symptom-badges/body-aches.png",
  "Discolored or bloody urine": "./assets/symptom-badges/bloody-urine.png",
  "Loss of smell or taste": "./assets/symptom-badges/loss-smell-taste.png",
  "Yellow skin/eyes": "./assets/symptom-badges/yellow-skin-eyes.png",
  Other: "./assets/symptom-badges/other.png",
});

export const DIAGNOSES = ["Influenza A", "Influenza B", "COVID-19", "Norovirus", "Strep Throat", "RSV", "Valley Fever", "Other"];

export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const WEEK_DAYS = ["S", "M", "T", "W", "T", "F", "S"];

export const ANIMAL_TYPE_OPTIONS = [
  ["bird", "Bird"],
  ["mammal", "Mammal"],
  ["reptile", "Reptile"],
  ["fish", "Fish"],
  ["amphibian", "Amphibian"],
  ["other", "Other"],
];

export const ANIMAL_TYPE_BADGES = Object.freeze({
  bird: "./assets/animal-type-badges/birds.png",
  mammal: "./assets/animal-type-badges/rodents.png",
  reptile: "./assets/animal-type-badges/reptiles.png",
  fish: "./assets/animal-type-badges/fish.png",
  amphibian: "./assets/animal-type-badges/amphibians.png",
  other: "./assets/animal-type-badges/other.png",
});

export const ANIMAL_GROUP_BADGES = Object.freeze({
  wildlife: "./assets/animal-type-badges/wildlife.png",
  livestock: "./assets/animal-type-badges/livestock.png",
  pet: "./assets/animal-type-badges/pets.png",
});

export const ENVIRONMENT_OBSERVATIONS = [
  ...ENVIRONMENTAL_CONCERN_OPTIONS,
];

export const CATEGORY_ORDER = ["people", "animals", "environment"];

export const CATEGORY_STEPS = {
  people: ["peopleSymptoms", "peopleAssessment"],
  animals: ["animalCategory", "animalSpecies", "animalCondition", "animalCount", "animalDate", "animalLocation"],
  environment: ["environmentIssue", "environmentConcern", "environmentLocation", "environmentDate"],
};

export const PEOPLE_SUBJECT_ORDER = ["self", "other"];

export const PHOTO_INPUT_ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif";

export const ALLOWED_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"]);

export const ALLOWED_IMAGE_EXTENSIONS = new Set([".jpg", ".jpeg", ".png", ".webp", ".heic", ".heif"]);

export const GENERIC_IMAGE_TYPES = new Set(["", "application/octet-stream"]);

export const MAX_PHOTOS_PER_CATEGORY = 3;

export const MAX_PHOTOS_PER_SUBMISSION = 6;

export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export const MAX_IMAGE_BYTES_PER_CATEGORY = 15 * 1024 * 1024;

export const MAX_SUBMISSION_IMAGE_BYTES = 25 * 1024 * 1024;

export const MAX_ATTACHMENT_UPLOAD_RETRIES = 3;

export const NO_RESIZE_IMAGE_QUALITY = 0.92;

export const RESIZED_IMAGE_QUALITY = 0.9;

export const MAX_IMAGE_RESIZE_ATTEMPTS = 20;

export const IMAGE_CONTENT_TYPES_BY_EXTENSION = new Map([
  [".jpg", "image/jpeg"],
  [".jpeg", "image/jpeg"],
  [".png", "image/png"],
  [".webp", "image/webp"],
  [".heic", "image/heic"],
  [".heif", "image/heif"],
]);

export const IMAGE_EXTENSIONS_BY_CONTENT_TYPE = new Map([
  ["image/jpeg", ".jpg"],
  ["image/png", ".png"],
  ["image/webp", ".webp"],
]);

export const ROUTES = Object.freeze({
  frontDoor: "/",
  peopleSubject: "/people/subject",
  peopleSelfFeeling: "/people/self/feeling",
  peopleSelfSymptoms: "/people/self/symptoms",
  peopleSelfAssessment: "/people/self/details",
  peopleSelfExposures: "/people/self/exposures",
  peopleSelfTravel: "/people/self/travel",
  peopleSelfGoodCheckIn: "/people/self/good-check-in",
  peopleSignIn: "/people/other/sign-in",
  peopleOtherFeeling: "/people/other/feeling",
  peopleOtherSymptoms: "/people/other/symptoms",
  peopleOtherAssessment: "/people/other/details",
  peopleOtherExposures: "/people/other/exposures",
  peopleOtherTravel: "/people/other/travel",
  peopleOtherGoodCheckIn: "/people/other/good-check-in",
  postSubmitRegistration: "/register",
  animalCategory: "/animals",
  animalSpecies: "/animals/species",
  animalCondition: "/animals/condition",
  animalCount: "/animals/count",
  animalDate: "/animals/date",
  animalLocation: "/animals/location",
  animalReporterFeeling: "/animals/reporter-feeling",
  environmentIssue: "/environment",
  environmentConcern: "/environment/concern",
  environmentLocation: "/environment/location",
  environmentDate: "/environment/date",
  environmentReporterFeeling: "/environment/reporter-feeling",
  reportLocation: "/location",
  done: "/thanks",
});

export const STEPS_BY_ROUTE = Object.freeze(
  Object.fromEntries(Object.entries(ROUTES).map(([stepName, route]) => [route, stepName]))
);


export const API_PORT = "8001";

const COMMUNITY_PULSE_LIMIT = 50;
const COMMUNITY_PULSE_POLL_MS = 30000;
const COMMUNITY_PULSE_TOOLTIPS_ENABLED = false;

export const PAGE_PARAMS = new URLSearchParams(window.location.search);

export const DEBUG_MAP = PAGE_PARAMS.get("debugMap") === "1" || PAGE_PARAMS.get("debugMode") === "1";

export const DEBUG_MODE = PAGE_PARAMS.get("debug") === "1" || DEBUG_MAP;

export let lastTrackedScreen = "";

export let reportFlowStarted = false;

export let accountSessionResolvedForAnalytics = false;

export let router = null;

export let isSubmitting = false;

export let isDetectingZip = false;

let ordinarySubmissionAttempt = null;

let isLoadingCommunityPulse = false;
let hasLoadedCommunityPulseSuccessfully = false;
let communityPulseDeferredRefresh = false;

export const photoProcessingStatuses = new WeakMap();
export const photoExifLocationResults = new Map();
export const photoProcessingStatusesByKey = new Map();
const communityPulseListFingerprints = new WeakMap();
const photoSelectionKeys = {
  animals: [],
  environment: [],
};

let nextReportPhotoKey = 1;
let reportLocationInitializationGeneration = 0;
let reportLocationInteractionGeneration = 0;
let zipDetectionGeneration = 0;


export const analytics = { initAnalytics, trackEvent, trackPageView };


export const I18N = {
  EN: {
    front_door_h: "What would you like to report today?",
    feeling_h: "How are you feeling today?",
    people_feeling_h: "How is the person feeling today?",
    feeling_s: "Your report helps Arizona detect health threats early.",
    subject_h: "Who is this report about?",
    subject_s: "This helps us separate the reporter from the person affected.",
    subject_self: "Myself",
    subject_self_d: "I am reporting about my own health",
    subject_other: "Another person",
    subject_other_d: "A child, relative, person I care for, or someone I observed",
    subject_signin_h: "Create an account to report about another person",
    subject_signin_s: "Your current report will be saved while you register. After email verification, you will return here to finish reports for yourself and another person.",
    subject_signin_cta: "Register and continue",
    subject_signin_save_error: "We could not save this report for registration. Your answers are still on this screen; check browser storage access and try again.",
    people_good_h: "Healthy people check-in",
    people_good_s: "No symptom details are needed. Please verify the reporting ZIP code for this check-in.",
    people_good_register_h: "Thank you for reporting.",
    people_good_register_s: "Would you like to register to be part of the i-detect community?",
    people_good_register_yes: "Yes",
    people_good_register_no: "Not at this time",
    header_sign_in: "Sign in",
    header_register: "Register",
    header_profile: "Account profile",
    header_sign_out: "Sign out",
    account_signed_out: "Signed out.",
    account_sign_out_error: "We could not sign you out. Please try again.",
    post_submit_register_h: "Thank you for reporting.",
    post_submit_register_s: "Would you like to register to be part of the i-detect community?",
    post_submit_register_yes: "Yes",
    post_submit_register_no: "Not at this time",
    report_zip_h: "Report ZIP code",
    zip_arizona: "Enter an Arizona ZIP code.",
    zip_detecting: "Detecting ZIP...",
    zip_detected: "Detected ZIP. Please confirm it is correct.",
    zip_detect_no_geo: "Location detection is not available in this browser. Enter ZIP manually.",
    zip_detect_denied: "Location permission was denied. Enter ZIP manually.",
    zip_detect_unavailable: "Could not detect your location. Enter ZIP manually.",
    zip_detect_timeout: "Location detection timed out. Enter ZIP manually.",
    zip_detect_no_zip: "Could not determine a ZIP code from your location. Enter ZIP manually.",
    zip_detect_not_arizona: "Detected location is outside Arizona. Enter an Arizona ZIP manually.",
    zip_detect_error: "Could not detect ZIP right now. Enter ZIP manually.",
    zip_format: "Enter a five-digit ZIP code, like 85701.",
    zip_required: "Enter an Arizona ZIP code.",
    sick: "Feeling Sick",
    sick_d: "Report symptoms, diagnosis, and time away",
    good: "Feeling Good",
    good_d: "Report environmental or animal observations",
    cat_h: "What is this about?",
    cat_s: "Select all that apply.",
    people: "People",
    people_d: "Yourself, family, or friends",
    animals: "Animals",
    animals_d: "Wildlife or livestock",
    env: "Environment",
    env_d: "Water, air, plants, or places",
    sym_h: "What symptoms are you having?",
    desc_sym: "Describe symptom...",
    how_many: "How many people are sick?",
    where: "Where are you located?",
    zip: "ZIP code",
    detect: "Detect",
    more_h: "A few more details",
    more_s: "This helps our analysis.",
    when_start: "When did symptoms start?",
    observed_date: "Date observed",
    select_date: "Select date",
    calendar_title: "Date symptoms started",
    calendar_observed_title: "Date observed",
    calendar_close: "Close calendar",
    calendar_previous_month: "Previous month",
    calendar_next_month: "Next month",
    prof_diag: "Was the person professionally diagnosed?",
    no_doc: "Hasn't seen a doctor",
    what_diag: "What was the diagnosis?",
    called_in_sick: "Did you call in sick to school/work?",
    exposures_h: "Possible exposures",
    exposures_s: "These questions help public health teams understand what may have happened before symptoms started.",
    exposure_mass_gathering: "Have you recently attended a public mass gathering before your symptoms started?",
    exposure_event: "What was the event?",
    exposure_live_animal: "Have you recently had contact with a live animal before your symptoms started?",
    exposure_bitten: "Were you bitten?",
    exposure_animal_sick: "Did the animal appear sick?",
    exposure_dead_animal: "Have you recently had contact with a dead animal before your symptoms started?",
    exposure_sick_individual: "Have you recently had contact with another sick individual before your symptoms started?",
    exposure_tick_insect: "Have you recently had a tick or insect bite before your symptoms started?",
    travel_h: "Recent travel",
    travel_s: "Travel history can help public health teams understand where exposure may have happened.",
    travel_question: "Have you recently traveled to another location or country before your symptoms started?",
    travel_location: "Where did you travel?",
    travel_location_placeholder: "Search city, region, or country",
    travel_location_searching: "Searching locations...",
    travel_location_empty: "No matching locations found. You can keep typing manually.",
    travel_location_error: "Location lookup is unavailable. You can enter the location manually.",
    travel_location_selected: "Selected standardized location.",
    obs_h: "What have you observed?",
    obs_s: "Report anything unusual in your environment.",
    people_report_h: "Tell us about the people affected",
    people_report_s: "Add symptoms, count, and location for this people report.",
    animal_report_h: "What is happening with animals?",
    animal_report_s: "Report unusual wildlife or livestock conditions.",
    animal_reporter_health_flow: "Animal reporter health",
    animal_category_h: "What type of sick or dead animal are you reporting?",
    animal_category_s: "Choose the animal domain so we can ask the right follow-up questions.",
    animal_category_livestock_d: "Livestock, poultry, or other domestic animals",
    animal_category_wildlife_d: "Wild birds, bats, rodents, reptiles, or other wildlife",
    animal_species_h: "Please select the species of animal",
    animal_species_s: "Pick the closest match from the current list.",
    animal_condition_h: "What is the condition of the animal?",
    animal_condition_s: "Tell us whether the animal is sick or dead.",
    animal_condition_wildlife_s: "For wildlife reports, the current report path accepts dead animals only.",
    animal_condition_sick_d: "The animal appears sick",
    animal_condition_dead_d: "The animal is dead",
    animal_count_h: "How many animals?",
    animal_count_s: "Enter the number for the selected condition.",
    animal_sick_count_question: "How many animals are sick?",
    animal_dead_count_question: "How many animals are dead?",
    animal_date_h: "When did you find the animal?",
    animal_date_s: "Choose a date from the past four weeks.",
    animal_date_error: "Choose a date from the past four weeks.",
    animal_location_h: "Where did you find the animal?",
    animal_location_s: "Enter the ZIP code. If a photo has GPS metadata, you can confirm a precise pin later.",
    animal_reporter_feeling_h: "Thank you for reporting this animal.",
    animal_reporter_feeling_s: "Please tell us how you are feeling.",
    animal_reporter_feeling_good: "Healthy",
    animal_reporter_feeling_sick: "Sick",
    animal_reporter_feeling_prefer_not_say: "Would rather not say",
    animal_species: "What species are you reporting?",
    animal_species_placeholder: "Type species...",
    animal_type: "What type of animal is it?",
    animal_group: "Is this animal wildlife or livestock?",
    animal_sick_count: "Number of sick animals",
    animal_dead_count: "Number of dead animals",
    environment_report_h: "What is happening in the environment?",
    environment_report_s: "Report unusual water, air, plant, or place conditions.",
    environment_reporter_health_flow: "Environment reporter health",
    environment_issue_h: "What issue or environmental concern are you reporting?",
    environment_issue_s: "Choose the concern type so we can ask the right follow-up questions.",
    environment_issue_insect_vectors: "Unusual Presence of Insect Vectors",
    environment_issue_insect_vectors_d: "Fleas, ticks, mosquitoes, or another insect vector",
    environment_issue_environmental_concern: "Environmental Concern",
    environment_issue_environmental_concern_d: "Flooding, contaminated water, raw sewage, air, plants, or places",
    environment_concern_h: "Please select the concern",
    environment_insect_concern_s: "Pick the insect vector you noticed.",
    environment_concern_s: "Select all environmental concerns that apply.",
    environment_location_h: "Where did you notice this environmental concern?",
    environment_location_s: "Enter the ZIP code. If a photo has GPS metadata, you can confirm a precise pin later.",
    environment_date_h: "When did you spot these conditions?",
    environment_date_s: "Choose a date from the past four weeks.",
    environment_date_error: "Choose a date from the past four weeks.",
    environment_reporter_feeling_h: "Thank you for reporting this environmental concern.",
    environment_reporter_feeling_s: "Please tell us how you are feeling.",
    environment_reporter_feeling_good: "Healthy",
    environment_reporter_feeling_sick: "Sick",
    environment_reporter_feeling_prefer_not_say: "Would rather not say",
    loc: "Your location",
    notes: "Notes (optional)",
    any_det: "Any additional details...",
    photo_h: "Photo (optional)",
    photo_add: "Choose Photos",
    photo_add_more: "Add More Photos",
    photo_remove: "Remove",
    photo_selected_one: "photo selected",
    photo_selected_many: "photos selected",
    photo_error_count: "Select up to 3 photos.",
    photo_error_submission_count: "Selected photos across this submission must total 6 or less.",
    photo_error_type: "Use JPEG, PNG, WebP, HEIC, or HEIF images only.",
    photo_error_size: "Each photo must be 5 MB or smaller.",
    photo_error_total: "Selected photos must total 15 MB or less.",
    photo_error_submission_total: "Selected photos across this submission must total 25 MB or less.",
    photo_status_processing: "Reducing this oversized photo...",
    photo_status_optimized: "Reduced without resizing",
    photo_status_resized: "Reduced and resized",
    photo_error_resize: "This browser could not resize this photo client-side. Choose a version that is 5 MB or smaller.",
    photo_exif_reading: "Reading EXIF location...",
    photo_exif_found: "GPS found",
    photo_exif_missing: "No GPS location found",
    photo_exif_error: "Could not read EXIF location",
    report_location_h: "Report location",
    report_location_s: "Review the pin and move it if needed before sharing the report.",
    report_location_searching: "Checking selected photos for GPS location...",
    report_location_unavailable: "Preparing an initial pin location.",
    report_location_available: "Confirm this pin to add precise coordinates to the report.",
    report_location_confirmed: "Precise report location confirmed.",
    report_location_declined: "Choose a location and confirm the pin before submitting.",
    report_location_map_error: "The map could not load. Confirm a precise location before submitting.",
    report_location_confirm: "Confirm location",
    report_location_coords: "Marker coordinates",
    report_location_unresolved: "Confirm the report location before submitting.",
    submission_success: "Your report was submitted successfully.",
    submission_partial_rejection: "We could not submit every report. Review your answers and try again.",
    submission_rejected: "Your report was not accepted. Review your answers and try again.",
    submission_malformed: "The report service returned an unexpected result. Please try again.",
    submission_server_failure: "The report service did not accept your report. Review your answers and try again.",
    submission_queued: "Your report was saved on this device. Select Submit Report to try again when the report service is available.",
    submission_queue_failed: "We could not submit or save your report. Your answers remain on this screen so you can try again.",
    thanks: "Thank you!",
    total_reports: "Total reports this week",
    pima: "across mapped Arizona ZIP codes",
    view_map: "View Map",
    rep_anon: "Report Another",
    submit: "Submit Report",
    continue: "Continue",
    map_aria_zip_report: "Arizona ZIP report map",
    map_aria_live_report_count: "Arizona live report count map",
    map_arizona_zip_codes: "Arizona ZIP codes",
    map_live_report: "Live report map",
    map_live_report_count: "Live report count map",
    map_find_zip: "Find a ZIP",
    map_zip_example: "e.g. 85003",
    map_search_zip_aria: "Search the map by ZIP code",
    map_go: "Go",
    map_layers: "Layers",
    map_people_reports: "People reports",
    map_time_window: "Time series",
    map_loading: "Loading map...",
    map_load_failed: "Mapbox failed to load",
    map_unavailable_detail: "The map is unavailable right now.",
    map_token_needed: "Mapbox token needed",
    map_token_help: "Open this page with ?mapboxToken=YOUR_TOKEN to render the Arizona map.",
    map_location_token_help: "Open this page with ?mapboxToken=YOUR_TOKEN to render the location map.",
    map_zip_invalid: "Enter a 5-digit ZIP code.",
    map_still_loading: "Map is still loading — try again in a moment.",
    map_boundaries_failed: "Could not load ZIP boundaries. Try again.",
    map_zip_not_found: "ZIP {zip} isn't in the Arizona map.",
    map_people: "People",
    map_animals: "Animals",
    map_environment: "Environment",
    map_show_category: "Show {category}",
    map_categories_shown: "Categories shown",
    map_legend: "Map legend",
    map_symptom: "Symptom",
    map_all_symptoms: "All symptoms",
    map_symptom_filter: "Symptom filter",
    map_filter_symptom: "Filter people reports by symptoms",
    map_symptoms_selected: "Symptoms selected",
    map_symptom_more_tag: "+{count} more",
    map_symptom_and_hint: "Showing only reports with all of these symptoms.",
    map_sick_reports: "Symptom reports",
    map_dot_size_total: "Dot size = symptom reports vs. other ZIPs shown",
    map_dot_size_symptom: "Dot size = {symptom} reports vs. other ZIPs shown",
    map_time_controls_aria: "Report map date controls",
    map_play: "Play",
    map_pause: "Pause",
    map_start: "Start",
    map_end: "End",
    map_start_date_aria: "Map start date",
    map_selected_date_aria: "Map selected date",
    map_period_from: "From {range}",
    map_period_through: "through",
    map_live_unavailable: "live data unavailable",
    map_sick_count_one: "1 symptom report",
    map_sick_count: "{sick} symptom reports",
    map_area_county: "County",
    map_area_zip: "ZIP",
    map_drill_hint: " Click a county to drill into its ZIP codes.",
    map_dots_sick_note: "Outbreak dots: red dots mark ZIPs with symptom reports, sized by how that ZIP compares to the other ZIPs shown.",
    map_shading_off_note: "{area} shading is off — pick Report volume, Per capita, or Full color above, or toggle ZIP dots.",
    map_shading_flat_note: "{area} shading: color = sick rate (yellow = lower, red = higher), every area at full color (no opacity difference).{drill} Grayed areas are insufficient data; hatched areas have no ZIP code.",
    map_shading_metric_note: "{area} shading: color = sick rate (yellow = lower, red = higher), opacity = {metric}.{drill} Grayed areas are insufficient data; hatched areas have no ZIP code.",
    map_metric_reports: "report volume",
    map_metric_rate: "reports per capita",
    map_showing_symptom: "Showing only people reports of \"{symptom}\". {note}",
    map_showing_symptoms: "Showing only people reports with all of \"{symptom}\". {note}",
    map_waiting_live: "Waiting for live read.community_report_count data.",
    map_waiting_live_detail: "Waiting for live {api} read.community_report_count data. Locally saved reports appear when present.",
    map_total_summary_one_zip: "{total} total reports ({people} people, {animals} animal, {environment} environment) across {zipCount} Arizona ZIP code {period}. {note}",
    map_total_summary_many_zips: "{total} total reports ({people} people, {animals} animal, {environment} environment) across {zipCount} Arizona ZIP codes {period}. {note}",
    ...COMMUNITY_REPORT_FORMATTER_I18N.EN,
  },
  ES: {
    front_door_h: "¿Qué quieres reportar hoy?",
    feeling_h: "¿Cómo te sientes hoy?",
    people_feeling_h: "¿Cómo se siente la persona hoy?",
    feeling_s: "Tu reporte ayuda a detectar amenazas de salud.",
    subject_h: "¿Sobre quién es este reporte?",
    subject_s: "Esto ayuda a separar a quien reporta de la persona afectada.",
    subject_self: "Yo mismo",
    subject_self_d: "Estoy reportando sobre mi propia salud",
    subject_other: "Otra persona",
    subject_other_d: "Un nino, familiar, persona a mi cuidado o alguien que observe",
    subject_signin_h: "Crea una cuenta para reportar sobre otra persona",
    subject_signin_s: "Tu reporte actual se guardara mientras te registras. Despues de verificar tu correo, volveras aqui para terminar los reportes sobre ti y otra persona.",
    subject_signin_cta: "Registrarse y continuar",
    subject_signin_save_error: "No pudimos guardar este reporte para el registro. Tus respuestas siguen en esta pantalla; revisa el acceso al almacenamiento del navegador e intentalo de nuevo.",
    people_good_h: "Chequeo saludable de personas",
    people_good_s: "No se necesitan detalles de sintomas. Verifica el codigo postal de este chequeo.",
    people_good_register_h: "Gracias por reportar.",
    people_good_register_s: "¿Quieres registrarte para ser parte de la comunidad i-detect?",
    people_good_register_yes: "Sí",
    people_good_register_no: "No por ahora",
    header_sign_in: "Iniciar sesión",
    header_register: "Registrarse",
    header_profile: "Perfil de la cuenta",
    header_sign_out: "Cerrar sesión",
    account_signed_out: "Sesión cerrada.",
    account_sign_out_error: "No pudimos cerrar tu sesión. Inténtalo de nuevo.",
    post_submit_register_h: "Gracias por reportar.",
    post_submit_register_s: "¿Quieres registrarte para ser parte de la comunidad i-detect?",
    post_submit_register_yes: "Sí",
    post_submit_register_no: "No por ahora",
    report_zip_h: "Codigo postal del reporte",
    zip_arizona: "Ingresa un codigo postal de Arizona.",
    zip_detecting: "Detectando codigo postal...",
    zip_detected: "Codigo postal detectado. Confirma que sea correcto.",
    zip_detect_no_geo: "La deteccion de ubicacion no esta disponible en este navegador. Ingresa el codigo postal manualmente.",
    zip_detect_denied: "Se nego el permiso de ubicacion. Ingresa el codigo postal manualmente.",
    zip_detect_unavailable: "No se pudo detectar tu ubicacion. Ingresa el codigo postal manualmente.",
    zip_detect_timeout: "La deteccion de ubicacion expiro. Ingresa el codigo postal manualmente.",
    zip_detect_no_zip: "No se pudo determinar un codigo postal desde tu ubicacion. Ingresa el codigo postal manualmente.",
    zip_detect_not_arizona: "La ubicacion detectada esta fuera de Arizona. Ingresa un codigo postal de Arizona manualmente.",
    zip_detect_error: "No se pudo detectar el codigo postal ahora. Ingresa el codigo postal manualmente.",
    zip_format: "Ingresa un codigo postal de cinco digitos, como 85701.",
    zip_required: "Ingresa un codigo postal de Arizona.",
    sick: "Me siento enfermo",
    sick_d: "Reportar sintomas, diagnostico y ausencias",
    good: "Me siento bien",
    good_d: "Reportar observaciones del entorno",
    cat_h: "¿De qué se trata?",
    cat_s: "Selecciona todo lo que aplique.",
    people: "Personas",
    people_d: "Tu, familia o amigos",
    animals: "Animales",
    animals_d: "Mascotas o vida silvestre",
    env: "Entorno",
    env_d: "Agua, aire, plantas o lugares",
    sym_h: "¿Qué síntomas tienes?",
    desc_sym: "Describe el sintoma...",
    how_many: "¿Cuántas personas están enfermas?",
    where: "¿Dónde te encuentras?",
    zip: "Codigo postal",
    detect: "Detectar",
    more_h: "Algunos detalles más",
    more_s: "Esto ayuda a nuestro analisis.",
    when_start: "¿Cuándo empezaron?",
    observed_date: "Fecha observada",
    select_date: "Selecciona fecha",
    calendar_title: "Fecha de inicio de sintomas",
    calendar_observed_title: "Fecha observada",
    calendar_close: "Cerrar calendario",
    calendar_previous_month: "Mes anterior",
    calendar_next_month: "Mes siguiente",
    prof_diag: "¿La persona fue diagnosticada?",
    no_doc: "No ha visto a un medico",
    what_diag: "¿Cuál fue el diagnóstico?",
    called_in_sick: "¿Llamaste para reportarte enfermo a escuela/trabajo?",
    exposures_h: "Posibles exposiciones",
    exposures_s: "Estas preguntas ayudan a los equipos de salud pública a entender qué pudo haber ocurrido antes de que empezaran los síntomas.",
    exposure_mass_gathering: "¿Asististe recientemente a una reunión pública grande antes de que empezaran los síntomas?",
    exposure_event: "¿Cuál fue el evento?",
    exposure_live_animal: "¿Tuviste contacto reciente con un animal vivo antes de que empezaran los síntomas?",
    exposure_bitten: "¿Te mordió?",
    exposure_animal_sick: "¿El animal parecía enfermo?",
    exposure_dead_animal: "¿Tuviste contacto reciente con un animal muerto antes de que empezaran los síntomas?",
    exposure_sick_individual: "¿Tuviste contacto reciente con otra persona enferma antes de que empezaran los síntomas?",
    exposure_tick_insect: "¿Tuviste recientemente una picadura de garrapata o insecto antes de que empezaran los síntomas?",
    travel_h: "Viaje reciente",
    travel_s: "El historial de viaje ayuda a los equipos de salud pública a entender dónde pudo ocurrir una exposición.",
    travel_question: "¿Viajaste recientemente a otra ubicación o país antes de que empezaran los síntomas?",
    travel_location: "¿A dónde viajaste?",
    travel_location_placeholder: "Busca ciudad, region o pais",
    travel_location_searching: "Buscando ubicaciones...",
    travel_location_empty: "No se encontraron ubicaciones. Puedes seguir escribiendo manualmente.",
    travel_location_error: "La busqueda de ubicacion no esta disponible. Puedes ingresar la ubicacion manualmente.",
    travel_location_selected: "Ubicacion estandarizada seleccionada.",
    obs_h: "¿Qué has observado?",
    obs_s: "Reporta algo inusual en tu entorno.",
    people_report_h: "Cuentanos sobre las personas afectadas",
    people_report_s: "Agrega sintomas, cantidad y ubicacion para este reporte de personas.",
    animal_report_h: "¿Qué está pasando con los animales?",
    animal_report_s: "Reporta condiciones inusuales en mascotas, ganado o vida silvestre.",
    animal_reporter_health_flow: "Salud de quien reporta animales",
    animal_category_h: "¿Qué tipo de animal enfermo o muerto estás reportando?",
    animal_category_s: "Elige el dominio del animal para hacer las preguntas correctas.",
    animal_category_livestock_d: "Ganado, aves de corral u otros animales domesticos",
    animal_category_wildlife_d: "Aves silvestres, murcielagos, roedores, reptiles u otra vida silvestre",
    animal_species_h: "Selecciona la especie del animal",
    animal_species_s: "Elige la opción más cercana de la lista actual.",
    animal_condition_h: "¿Cuál es la condición del animal?",
    animal_condition_s: "Indica si el animal está enfermo o muerto.",
    animal_condition_wildlife_s: "Para reportes de vida silvestre, este flujo acepta solo animales muertos.",
    animal_condition_sick_d: "El animal parece enfermo",
    animal_condition_dead_d: "El animal está muerto",
    animal_count_h: "¿Cuántos animales?",
    animal_count_s: "Ingresa el número para la condición seleccionada.",
    animal_sick_count_question: "¿Cuántos animales están enfermos?",
    animal_dead_count_question: "¿Cuántos animales están muertos?",
    animal_date_h: "¿Cuándo encontraste el animal?",
    animal_date_s: "Elige una fecha de las últimas cuatro semanas.",
    animal_date_error: "Elige una fecha de las últimas cuatro semanas.",
    animal_location_h: "¿Dónde encontraste el animal?",
    animal_location_s: "Ingresa el código postal. Si una foto tiene metadatos GPS, podrás confirmar un pin preciso después.",
    animal_reporter_feeling_h: "Gracias por reportar este animal.",
    animal_reporter_feeling_s: "Dinos cómo te sientes.",
    animal_reporter_feeling_good: "Saludable",
    animal_reporter_feeling_sick: "Enfermo",
    animal_reporter_feeling_prefer_not_say: "Prefiero no decir",
    animal_species: "¿Qué especie estás reportando?",
    animal_species_placeholder: "Escribe la especie...",
    animal_type: "¿Qué tipo de animal es?",
    animal_group: "¿Es vida silvestre, ganado o mascota?",
    animal_sick_count: "Numero de animales enfermos",
    animal_dead_count: "Numero de animales muertos",
    environment_report_h: "¿Qué está pasando en el entorno?",
    environment_report_s: "Reporta condiciones inusuales del agua, aire, plantas o lugares.",
    environment_reporter_health_flow: "Salud de quien reporta el entorno",
    environment_issue_h: "¿Qué problema o preocupación ambiental estás reportando?",
    environment_issue_s: "Elige el tipo de preocupación para hacer las preguntas correctas.",
    environment_issue_insect_vectors: "Presencia inusual de insectos vectores",
    environment_issue_insect_vectors_d: "Pulgas, garrapatas, mosquitos u otro insecto vector",
    environment_issue_environmental_concern: "Preocupación ambiental",
    environment_issue_environmental_concern_d: "Inundación, agua contaminada, aguas residuales, aire, plantas o lugares",
    environment_concern_h: "Selecciona la preocupación",
    environment_insect_concern_s: "Elige el insecto vector que notaste.",
    environment_concern_s: "Selecciona todas las preocupaciones ambientales que apliquen.",
    environment_location_h: "¿Dónde notaste esta preocupación ambiental?",
    environment_location_s: "Ingresa el código postal. Si una foto tiene metadatos GPS, podrás confirmar un pin preciso después.",
    environment_date_h: "¿Cuándo viste estas condiciones?",
    environment_date_s: "Elige una fecha de las últimas cuatro semanas.",
    environment_date_error: "Elige una fecha de las últimas cuatro semanas.",
    environment_reporter_feeling_h: "Gracias por reportar esta preocupación ambiental.",
    environment_reporter_feeling_s: "Dinos cómo te sientes.",
    environment_reporter_feeling_good: "Saludable",
    environment_reporter_feeling_sick: "Enfermo",
    environment_reporter_feeling_prefer_not_say: "Prefiero no decir",
    loc: "Tu ubicacion",
    notes: "Notas (opcional)",
    any_det: "Detalles adicionales...",
    photo_h: "Foto (opcional)",
    photo_add: "Elegir fotos",
    photo_add_more: "Agregar mas fotos",
    photo_remove: "Quitar",
    photo_selected_one: "foto seleccionada",
    photo_selected_many: "fotos seleccionadas",
    photo_error_count: "Selecciona hasta 3 fotos.",
    photo_error_submission_count: "Las fotos seleccionadas en este envio deben sumar 6 o menos.",
    photo_error_type: "Usa solo imagenes JPEG, PNG, WebP, HEIC o HEIF.",
    photo_error_size: "Cada foto debe ser de 5 MB o menos.",
    photo_error_total: "Las fotos seleccionadas deben sumar 15 MB o menos.",
    photo_error_submission_total: "Las fotos seleccionadas en este envio deben sumar 25 MB o menos.",
    photo_status_processing: "Reduciendo esta foto de gran tamano...",
    photo_status_optimized: "Reducida sin cambiar sus dimensiones",
    photo_status_resized: "Reducida y redimensionada",
    photo_error_resize: "Este navegador no pudo redimensionar esta foto. Elige una version de 5 MB o menos.",
    photo_exif_reading: "Leyendo ubicacion EXIF...",
    photo_exif_found: "GPS encontrado",
    photo_exif_missing: "No se encontro ubicacion GPS",
    photo_exif_error: "No se pudo leer la ubicacion EXIF",
    report_location_h: "Ubicacion del reporte",
    report_location_s: "Revisa el pin y muevelo si es necesario antes de enviar el reporte.",
    report_location_searching: "Buscando ubicacion GPS en las fotos seleccionadas...",
    report_location_unavailable: "Preparando una ubicacion inicial para el pin.",
    report_location_available: "Confirma este pin para agregar coordenadas precisas al reporte.",
    report_location_confirmed: "Ubicacion precisa confirmada.",
    report_location_declined: "Elige una ubicacion y confirma el pin antes de enviar.",
    report_location_map_error: "No se pudo cargar el mapa. Confirma una ubicacion precisa antes de enviar.",
    report_location_confirm: "Confirmar ubicacion",
    report_location_coords: "Coordenadas del pin",
    report_location_unresolved: "Confirma la ubicacion del reporte antes de enviar.",
    submission_success: "Tu reporte se envio correctamente.",
    submission_partial_rejection: "No pudimos enviar todos los reportes. Revisa tus respuestas e intentalo de nuevo.",
    submission_rejected: "Tu reporte no fue aceptado. Revisa tus respuestas e intentalo de nuevo.",
    submission_malformed: "El servicio de reportes devolvio un resultado inesperado. Intentalo de nuevo.",
    submission_server_failure: "El servicio de reportes no acepto tu reporte. Revisa tus respuestas e intentalo de nuevo.",
    submission_queued: "Tu reporte se guardo en este dispositivo. Selecciona Enviar reporte para volver a intentarlo cuando el servicio este disponible.",
    submission_queue_failed: "No pudimos enviar ni guardar tu reporte. Tus respuestas permanecen en esta pantalla para que puedas intentarlo de nuevo.",
    thanks: "Gracias!",
    total_reports: "Reportes totales esta semana",
    pima: "en codigos postales mapeados de Arizona",
    view_map: "Ver mapa",
    rep_anon: "Reportar otro",
    submit: "Enviar reporte",
    continue: "Continuar",
    map_aria_zip_report: "Mapa de reportes por códigos postales de Arizona",
    map_aria_live_report_count: "Mapa en vivo del conteo de reportes de Arizona",
    map_arizona_zip_codes: "Códigos postales de Arizona",
    map_live_report: "Mapa de reportes en vivo",
    map_live_report_count: "Mapa en vivo del conteo de reportes",
    map_find_zip: "Buscar un código postal",
    map_zip_example: "p. ej. 85003",
    map_search_zip_aria: "Buscar en el mapa por código postal",
    map_go: "Ir",
    map_layers: "Capas",
    map_people_reports: "Reportes de personas",
    map_time_window: "Serie de tiempo",
    map_loading: "Cargando mapa...",
    map_load_failed: "No se pudo cargar Mapbox",
    map_unavailable_detail: "El mapa no está disponible en este momento.",
    map_token_needed: "Se necesita un token de Mapbox",
    map_token_help: "Abre esta página con ?mapboxToken=YOUR_TOKEN para mostrar el mapa de Arizona.",
    map_location_token_help: "Abre esta página con ?mapboxToken=YOUR_TOKEN para mostrar el mapa de ubicación.",
    map_zip_invalid: "Ingresa un código postal de 5 dígitos.",
    map_still_loading: "El mapa todavía se está cargando; inténtalo de nuevo en un momento.",
    map_boundaries_failed: "No se pudieron cargar los límites de los códigos postales. Inténtalo de nuevo.",
    map_zip_not_found: "El código postal {zip} no está en el mapa de Arizona.",
    map_people: "Personas",
    map_animals: "Animales",
    map_environment: "Medio ambiente",
    map_show_category: "Mostrar {category}",
    map_categories_shown: "Categorías mostradas",
    map_legend: "Leyenda del mapa",
    map_symptom: "Síntoma",
    map_all_symptoms: "Todos los síntomas",
    map_symptom_filter: "Filtro de síntomas",
    map_filter_symptom: "Filtrar reportes de personas por síntomas",
    map_symptoms_selected: "Síntomas seleccionados",
    map_symptom_more_tag: "+{count} más",
    map_symptom_and_hint: "Se muestran solo los reportes con todos estos síntomas.",
    map_sick_reports: "Reportes de síntomas",
    map_dot_size_total: "Tamaño del punto = reportes de síntomas frente a los demás códigos postales mostrados",
    map_dot_size_symptom: "Tamaño del punto = reportes de {symptom} frente a los demás códigos postales mostrados",
    map_time_controls_aria: "Controles de fecha del mapa de reportes",
    map_play: "Reproducir",
    map_pause: "Pausar",
    map_start: "Inicio",
    map_end: "Fin",
    map_start_date_aria: "Fecha inicial del mapa",
    map_selected_date_aria: "Fecha seleccionada del mapa",
    map_period_from: "Del {range}",
    map_period_through: "al",
    map_live_unavailable: "datos en vivo no disponibles",
    map_sick_count_one: "1 reporte de síntomas",
    map_sick_count: "{sick} reportes de síntomas",
    map_area_county: "Condado",
    map_area_zip: "Código postal",
    map_drill_hint: " Selecciona un condado para ver sus códigos postales.",
    map_dots_sick_note: "Puntos de brote: los puntos rojos marcan códigos postales con reportes de síntomas y su tamaño refleja cómo se compara ese código postal con los demás mostrados.",
    map_shading_off_note: "El sombreado por {area} está desactivado; elige Volumen de reportes, Por habitante o Color completo, o activa los puntos de códigos postales.",
    map_shading_flat_note: "Sombreado por {area}: el color representa la tasa de enfermedad (amarillo = menor, rojo = mayor) y todas las áreas usan color completo (sin diferencia de opacidad).{drill} Las áreas grises no tienen datos suficientes; las áreas rayadas no tienen código postal.",
    map_shading_metric_note: "Sombreado por {area}: el color representa la tasa de enfermedad (amarillo = menor, rojo = mayor) y la opacidad representa {metric}.{drill} Las áreas grises no tienen datos suficientes; las áreas rayadas no tienen código postal.",
    map_metric_reports: "volumen de reportes",
    map_metric_rate: "reportes por habitante",
    map_showing_symptom: "Se muestran solo reportes de personas con \"{symptom}\". {note}",
    map_showing_symptoms: "Se muestran solo reportes de personas con todos estos síntomas: \"{symptom}\". {note}",
    map_waiting_live: "Esperando datos en vivo de read.community_report_count.",
    map_waiting_live_detail: "Esperando datos en vivo de read.community_report_count desde {api}. Los reportes guardados localmente aparecerán cuando estén disponibles.",
    map_total_summary_one_zip: "{total} reportes totales ({people} de personas, {animals} de animales, {environment} del medio ambiente) en {zipCount} código postal de Arizona {period}. {note}",
    map_total_summary_many_zips: "{total} reportes totales ({people} de personas, {animals} de animales, {environment} del medio ambiente) en {zipCount} códigos postales de Arizona {period}. {note}",
    ...COMMUNITY_REPORT_FORMATTER_I18N.ES,
  },
};


export function createExposureState() {
  return {
    massGathering: "",
    massGatheringEvent: "",
    liveAnimalContact: "",
    liveAnimalBitten: "",
    liveAnimalAppearedSick: "",
    deadAnimalContact: "",
    sickIndividualContact: "",
    tickOrInsectBite: "",
    recentTravel: "",
    recentTravelLocation: "",
    recentTravelLocationSelection: null,
    recentTravelLocationFallbackAllowed: false,
  };
}

export function createPeopleReportState() {
  return {
    feeling: "",
    symptoms: [],
    otherSymptom: "",
    sickCount: "1",
    zip: "",
    locationH3: null,
    onset: "",
    diagnosed: "",
    diagnosis: "",
    calledInSick: "",
    exposures: createExposureState(),
  };
}


const MAP_LAB_PLAYBACK_MS = 650;
const defaultMapTimeWindow = defaultReportMapTimeWindow();
let mapPlaybackTimer = null;
const accountSessionRequestGuard = createAccountSessionRequestGuard();
const travelLocationSearch = createLocationAutocompleteSearch({
  tokenProvider: mapboxToken,
});


export const state = {
  step: 0,
  lang: storedSiteLanguage(localStorage),
  theme: storedSiteTheme(localStorage),
  reportSubjects: [],
  accountSignedIn: false,
  accountEmail: "",
  accountPostalCode: "",
  accountReportSubmissionBatchId: null,
  selfReportUsesAccountPostalCode: false,
  peopleReports: {
    self: createPeopleReportState(),
    other: createPeopleReportState(),
  },
  categories: [],
  animalSpecies: "",
  animalType: "",
  animalGroup: "",
  animalStatus: "",
  animalSickCount: "",
  animalDeadCount: "",
  animalObservedDate: "",
  animalObservations: [],
  animalZip: "",
  animalNotes: "",
  animalPhotos: [],
  animalPhotoError: "",
  animalReporterFeeling: "",
  environmentIssueType: "",
  insectVectorType: "",
  environmentConcerns: [],
  environmentObservedDate: "",
  environmentObservations: [],
  environmentZip: "",
  environmentNotes: "",
  environmentPhotos: [],
  environmentPhotoError: "",
  environmentReporterFeeling: "",
  reportLocation: createReportLocationState(),
  zipDetectField: "",
  zipDetectMessage: "",
  zipDetectMessageKind: "",
  datePickerTarget: "",
  calendarMonth: "",
  communityTotalReportCount: null,
  communityReportSummary: null,
  mapTimeWindowStartDate: defaultMapTimeWindow.windowStartDate,
  mapTimeWindowEndDate: defaultMapTimeWindow.windowEndDate,
  mapTimeStartDate: defaultMapTimeWindow.startDate,
  mapTimeSelectedDate: defaultMapTimeWindow.selectedDate,
  mapTimePlaybackRunning: false,
  communityPulseReceipts: communityPulse.loadReceipts(localStorage),
  communityPulseBackendItems: [],
  communityPulseScope: "all",
  communityPulseCounty: "",
  communityPulseStatus: "loading",
  // Symptom filter for people reports: "" = all symptoms; else a single symptom label.
  mapLabSymptoms: [],
  travelAutocomplete: emptyLocationAutocompleteState(),
};


export function ensureAccountReportSubmissionBatch(randomUUID = () => crypto.randomUUID()) {
  if (!state.accountReportSubmissionBatchId) {
    state.accountReportSubmissionBatchId = String(randomUUID()).toLowerCase();
  }
  return state.accountReportSubmissionBatchId;
}


export function clearAccountReportSubmissionBatch(storage) {
  clearAccountReportSubmissionIds(storage);
  state.accountReportSubmissionBatchId = null;
}


export const screen = document.getElementById("screen");

export const nextButton = document.getElementById("nextButton");

export const backButton = document.getElementById("backButton");

export const progressDots = document.getElementById("progressDots");

export const signInLink = document.getElementById("signInLink");

export const registerLink = document.getElementById("registerLink");

export const dataMapsLink = document.getElementById("dataMapsLink");

export const signedInLink = document.getElementById("signedInLink");

export const signOutMenuItem = document.getElementById("signOutMenuItem");

export const actionBar = document.getElementById("actionBar");
// The action bar's original home (the report stage footer slot). On the front door we
// relocate the bar into the scroll flow above the map, so we need its home to restore it.

export const actionBarHome = actionBar.parentElement;

export const reportStatus = document.getElementById("reportStatus");

export const payloadPreview = document.getElementById("payloadPreview");

export const submitStatus = document.getElementById("submitStatus");

export const communityTotalReportCount = document.getElementById("communityTotalReportCount");

export const communityTotalReportCountLabel = document.getElementById("communityTotalReportCountLabel");

export const debugTrigger = document.getElementById("debugTrigger");

export const debugPanel = document.getElementById("debugPanel");

export const debugBackdrop = document.getElementById("debugBackdrop");

export const siteSettingsMenu = initializeSiteSettingsMenu({
  documentRoot: document,
  storage: localStorage,
  copy: (language, key) => I18N[language]?.[key] || "",
  getLanguage: () => state.lang,
  getTheme: () => state.theme,
  onLanguageChange: (language) => {
    state.lang = language;
    render();
  },
  onThemeChange: (theme) => {
    state.theme = theme;
    render();
  },
});

setMapRuntime({
  state,
  DEBUG_MAP,
  DEBUG_MODE,
  debugPanel,
  apiOrigin,
  copy,
  countCopy,
  escapeHtml,
  formatCopy,
  language: () => state.lang,
  renderCommunityReportCount,
});


export function setSubmissionStatus(userMessage, debugMessage = userMessage) {
  reportStatus.textContent = userMessage;
  reportStatus.hidden = !userMessage;
  submitStatus.textContent = debugMessage;
}


export function clearUserSubmissionStatus() {
  reportStatus.textContent = "";
  reportStatus.hidden = true;
}


export function setDebugPanelOpen(isOpen) {
  if (!DEBUG_MODE) return;
  debugPanel.hidden = !isOpen;
  debugBackdrop.hidden = !isOpen;
  debugTrigger.setAttribute("aria-expanded", String(isOpen));
  document.body.classList.toggle("debug-panel-open", isOpen);

  if (isOpen) {
    renderReportMaps();
    document.getElementById("closeDebug").focus();
  } else {
    debugTrigger.focus();
  }
}


export function apiOrigin() {
  localStorage.removeItem("community-report-api-origin");
  return defaultApiOrigin();
}


export function defaultApiOrigin() {
  if (window.location.protocol === "file:") {
    return `http://localhost:${API_PORT}`;
  }
  return window.location.origin;
}


export function apiUrl(path) {
  return new URL(path, apiOrigin()).toString();
}


export function t() {
  return I18N[state.lang] || I18N.EN;
}


export function copy(key) {
  return t()[key] || I18N.EN[key] || "";
}


export function formatCopy(key, replacements = {}) {
  return interpolateCommunityReportCopy(copy(key), replacements);
}


export function countCopy(oneKey, manyKey, count, replacements = {}) {
  return formatCopy(count === 1 ? oneKey : manyKey, { count, ...replacements });
}


export function selectedCategories() {
  return CATEGORY_ORDER.filter((category) => state.categories.includes(category));
}


export function selectedReportLocationCategories() {
  return selectedCategories().filter((category) => category === "animals" || category === "environment");
}


export function createReportPhotoKey() {
  const key = `photo-${nextReportPhotoKey}`;
  nextReportPhotoKey += 1;
  return key;
}


export function photoKeysForCategory(category) {
  return photoSelectionKeys[category] || [];
}


export function reportPhotoKeyForSelection(_file, index, category) {
  return photoKeysForCategory(category)[index] || "";
}


export function appendReportPhotoKeys(category, count) {
  const keys = Array.from({ length: count }, () => createReportPhotoKey());
  photoKeysForCategory(category).push(...keys);
  return keys;
}


export function removeReportPhotoKey(category, index) {
  const [removedKey] = photoKeysForCategory(category).splice(index, 1);
  if (removedKey) {
    photoExifLocationResults.delete(removedKey);
    photoProcessingStatusesByKey.delete(removedKey);
  }
  return removedKey || "";
}


export function resetReportPhotoKeys() {
  photoSelectionKeys.animals = [];
  photoSelectionKeys.environment = [];
}


export function currentReportLocationPhotoSources() {
  return orderedReportPhotoSources({
    animalPhotos: state.animalPhotos,
    environmentPhotos: state.environmentPhotos,
  }, reportPhotoKeyForSelection);
}


export function selectedReportLocationPhotoCount() {
  return state.animalPhotos.length + state.environmentPhotos.length;
}


export function shouldIncludeReportLocationStep() {
  return shouldIncludeReportLocationStepForState({
    hasReportLocationCategory: selectedReportLocationCategories().length > 0,
    photoCount: selectedReportLocationPhotoCount(),
    status: state.reportLocation.status,
  });
}


export function selectedExactLocationZip() {
  const categories = selectedReportLocationCategories();
  if (categories.includes("animals") && state.animalZip) return state.animalZip;
  if (categories.includes("environment") && state.environmentZip) return state.environmentZip;
  return "";
}


export function clearReportLocationForZipChange() {
  if (!selectedReportLocationCategories().length) return;
  reportLocationInteractionGeneration += 1;
  state.reportLocation = reportLocationZipChanged(state.reportLocation);
}


export function formatReportCoordinates(coordinates) {
  if (!isValidCoordinatePair(coordinates)) return "";
  return `${coordinates.latitude.toFixed(5)}, ${coordinates.longitude.toFixed(5)}`;
}


export function selectedPeopleSubjects() {
  return PEOPLE_SUBJECT_ORDER.filter((subject) => state.reportSubjects.includes(subject));
}


export function categoryLabel(category) {
  if (category === "environment") return copy("env");
  return copy(category);
}


export function steps() {
  const stepList = [
    "frontDoor",
    ...(state.categories.includes("people") ? ["peopleSubject"] : []),
  ];

  selectedCategories().forEach((category) => {
    if (category === "people") {
      if (state.reportSubjects.includes("self")) {
        stepList.push(...peopleReportSteps("self"));
      }
      if (state.reportSubjects.includes("other")) {
        stepList.push(...(state.accountSignedIn ? peopleReportSteps("other") : ["peopleSignIn"]));
      }
      return;
    }
    stepList.push(...(CATEGORY_STEPS[category] || []));
  });

  if (shouldIncludeReportLocationStep()) stepList.push("reportLocation");
  if (shouldAskAnimalReporterFeeling()) {
    stepList.push("animalReporterFeeling");
    if (state.animalReporterFeeling === "good") stepList.push("peopleSelfGoodCheckIn");
    if (state.animalReporterFeeling === "sick") {
      stepList.push("peopleSelfSymptoms", "peopleSelfAssessment", "peopleSelfExposures", "peopleSelfTravel");
    }
  }
  if (shouldAskEnvironmentReporterFeeling()) {
    stepList.push("environmentReporterFeeling");
    if (state.environmentReporterFeeling === "good") stepList.push("peopleSelfGoodCheckIn");
    if (state.environmentReporterFeeling === "sick") {
      stepList.push("peopleSelfSymptoms", "peopleSelfAssessment", "peopleSelfExposures", "peopleSelfTravel");
    }
  }
  stepList.push(POST_SUBMIT_REGISTRATION_STEP);
  stepList.push("done");
  return stepList;
}


export function shouldAskAnimalReporterFeeling() {
  return state.categories.includes("animals") && !state.categories.includes("people") && !state.categories.includes("environment");
}


export function shouldAskEnvironmentReporterFeeling() {
  return state.categories.includes("environment") && !state.categories.includes("people");
}


export function animalReporterAddsPeople() {
  return shouldAskAnimalReporterFeeling() && ["good", "sick"].includes(state.animalReporterFeeling);
}


export function environmentReporterAddsPeople() {
  return shouldAskEnvironmentReporterFeeling() && ["good", "sick"].includes(state.environmentReporterFeeling);
}


export function reporterAddsPeople() {
  return animalReporterAddsPeople() || environmentReporterAddsPeople();
}


export function peopleReportSteps(subject) {
  const report = state.peopleReports[subject];
  const prefix = peopleStepPrefix(subject);
  const reportSteps = [`${prefix}Feeling`];
  if (report.feeling === "sick") {
    reportSteps.push(`${prefix}Symptoms`, `${prefix}Assessment`, `${prefix}Exposures`, `${prefix}Travel`);
  }
  if (report.feeling === "good") reportSteps.push(`${prefix}GoodCheckIn`);
  return reportSteps;
}


export function peopleStepPrefix(subject) {
  return subject === "other" ? "peopleOther" : "peopleSelf";
}


export function peopleSubjectForStep(stepName = currentStep()) {
  if (stepName.startsWith("peopleOther") || stepName === "peopleSignIn") return "other";
  if (stepName.startsWith("peopleSelf")) return "self";
  const selectedSubjects = selectedPeopleSubjects();
  return selectedSubjects[0] || "self";
}


export function currentPeopleReport() {
  return state.peopleReports[peopleSubjectForStep()];
}


export function peopleSubjectLabel(subject = peopleSubjectForStep()) {
  return subject === "other" ? copy("subject_other") : copy("subject_self");
}


export function isPeopleReportStep(stepName = currentStep()) {
  return stepName.startsWith("peopleSelf") || stepName.startsWith("peopleOther");
}


export function isPeopleFeelingStep(stepName = currentStep()) {
  return stepName.endsWith("Feeling") && isPeopleReportStep(stepName);
}


export function isPeopleSymptomsStep(stepName = currentStep()) {
  return stepName.endsWith("Symptoms") && isPeopleReportStep(stepName);
}


export function isPeopleAssessmentStep(stepName = currentStep()) {
  return stepName.endsWith("Assessment") && isPeopleReportStep(stepName);
}


export function isPeopleExposuresStep(stepName = currentStep()) {
  return stepName.endsWith("Exposures") && isPeopleReportStep(stepName);
}


export function isPeopleTravelStep(stepName = currentStep()) {
  return stepName.endsWith("Travel") && isPeopleReportStep(stepName);
}


export function isPeopleGoodCheckInStep(stepName = currentStep()) {
  return stepName.endsWith("GoodCheckIn") && isPeopleReportStep(stepName);
}


export function peopleReportFlowLabel(subject = peopleSubjectForStep()) {
  const flowLabel = reportFlowLabel("people") || copy("animal_reporter_health_flow");
  return `${flowLabel} - ${peopleSubjectLabel(subject)}`;
}


export function reportCategoryForStep(stepName = currentStep()) {
  if (isPeopleReportStep(stepName)) return "people";
  if (stepName.startsWith("animal")) return "animals";
  if (stepName.startsWith("environment")) return "environment";
  return "";
}


export function reportFlowLabel(category) {
  const categories = selectedCategories();
  const sectionNumber = categories.indexOf(category) + 1;
  if (sectionNumber <= 0) return "";
  return `Section ${sectionNumber} of ${categories.length} - ${categoryLabel(category)}`;
}


export function observationFieldNames(category) {
  if (category === "animals") {
    return {
      observations: "animalObservations",
      date: "animalObservedDate",
      zip: "animalZip",
      notes: "animalNotes",
      photos: "animalPhotos",
      photoError: "animalPhotoError",
      photoInputId: "animalPhotoInput",
    };
  }
  return {
    observations: "environmentObservations",
    date: "environmentObservedDate",
    zip: "environmentZip",
    notes: "environmentNotes",
    photos: "environmentPhotos",
    photoError: "environmentPhotoError",
    photoInputId: "environmentPhotoInput",
  };
}


export function observationOptions(category) {
  return category === "environment" ? ENVIRONMENTAL_CONCERN_OPTIONS : [];
}


export function escapeHtml(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}


export function padDatePart(value) {
  return String(value).padStart(2, "0");
}


export function isoDateFromDate(date) {
  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;
}


export function todayIsoDate(now = new Date()) {
  return arizonaIsoDate(now);
}


export function isFutureIsoDate(value, today = todayIsoDate()) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || "")) && value > today;
}


export function parseIsoDate(value) {
  const match = String(value || "").trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number.parseInt(match[1], 10);
  const month = Number.parseInt(match[2], 10) - 1;
  const day = Number.parseInt(match[3], 10);
  const date = new Date(year, month, day);
  if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) return null;
  return date;
}


export function parseLegacyOnsetDate(value) {
  const normalized = String(value || "").trim().toLowerCase();
  const today = new Date();
  if (normalized === "today") return today;
  if (normalized === "yesterday") return new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
  if (normalized === "this week") return today;
  if (normalized === "last week") return new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
  return null;
}


export function parseOnsetDate(value) {
  return parseIsoDate(value) || parseLegacyOnsetDate(value);
}


export function displayDate(value) {
  const date = parseOnsetDate(value);
  if (!date) return "";
  return `${padDatePart(date.getMonth() + 1)}/${padDatePart(date.getDate())}/${date.getFullYear()}`;
}


export function monthKeyForDate(date) {
  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}`;
}


export function dateForMonthKey(monthKey) {
  const match = String(monthKey || "").match(/^(\d{4})-(\d{2})$/);
  if (!match) return new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  return new Date(Number.parseInt(match[1], 10), Number.parseInt(match[2], 10) - 1, 1);
}


export function calendarDays(monthKey) {
  const monthDate = dateForMonthKey(monthKey);
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const lastDate = new Date(year, month + 1, 0).getDate();
  const days = Array.from({ length: firstDay }, () => "");
  for (let day = 1; day <= lastDate; day += 1) {
    days.push(isoDateFromDate(new Date(year, month, day)));
  }
  while (days.length % 7 !== 0) days.push("");
  return days;
}


export function photoSummaryText(files) {
  if (files.length === 1) return `1 ${copy("photo_selected_one")}`;
  return `${files.length} ${copy("photo_selected_many")}`;
}


export function formatFileSize(bytes) {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}


export function fileExtension(name) {
  const lastDot = String(name).lastIndexOf(".");
  if (lastDot === -1) return "";
  return String(name).slice(lastDot).toLowerCase();
}


export function isAllowedImageFile(file) {
  const contentType = String(file.type || "").trim().toLowerCase();
  if (ALLOWED_IMAGE_TYPES.has(contentType)) return true;
  if (!GENERIC_IMAGE_TYPES.has(contentType)) return false;
  return ALLOWED_IMAGE_EXTENSIONS.has(fileExtension(file.name));
}


export function totalPhotoBytes(files) {
  return files.reduce((sum, file) => sum + file.size, 0);
}


export function validatePhotoSelection(existingFiles, candidateFiles, enforceSizeLimits = false) {
  const combined = [...existingFiles, ...candidateFiles];
  if (combined.length > MAX_PHOTOS_PER_CATEGORY) return copy("photo_error_count");
  if (!candidateFiles.every(isAllowedImageFile)) return copy("photo_error_type");
  if (enforceSizeLimits && !candidateFiles.every((file) => file.size <= MAX_IMAGE_BYTES)) {
    return copy("photo_error_size");
  }
  if (enforceSizeLimits && totalPhotoBytes(combined) > MAX_IMAGE_BYTES_PER_CATEGORY) {
    return copy("photo_error_total");
  }
  return "";
}


export function selectedPhotoCategories() {
  return selectedCategories().filter((category) => category === "animals" || category === "environment");
}


export function photoFilesForCategory(category) {
  if (category !== "animals" && category !== "environment") return [];
  const fields = observationFieldNames(category);
  return state[fields.photos];
}


export function validateExistingPhotoSelection(files, enforceSizeLimits) {
  return validatePhotoSelection([], files, enforceSizeLimits);
}


export function validateSubmissionPhotos(enforceSizeLimits = false) {
  ["animals", "environment"].forEach((category) => {
    const fields = observationFieldNames(category);
    state[fields.photoError] = "";
  });

  const categories = selectedPhotoCategories();
  let totalFiles = 0;
  let totalBytes = 0;
  let firstError = "";

  categories.forEach((category) => {
    const fields = observationFieldNames(category);
    const files = photoFilesForCategory(category);
    const error = validateExistingPhotoSelection(files, enforceSizeLimits);
    state[fields.photoError] = error;
    if (!firstError && error) firstError = error;
    totalFiles += files.length;
    totalBytes += totalPhotoBytes(files);
  });

  if (!firstError && totalFiles > MAX_PHOTOS_PER_SUBMISSION) {
    firstError = copy("photo_error_submission_count");
    categories.forEach((category) => {
      const fields = observationFieldNames(category);
      if (state[fields.photos].length) state[fields.photoError] = firstError;
    });
  }

  if (!firstError && enforceSizeLimits && totalBytes > MAX_SUBMISSION_IMAGE_BYTES) {
    firstError = copy("photo_error_submission_total");
    categories.forEach((category) => {
      const fields = observationFieldNames(category);
      if (state[fields.photos].length) state[fields.photoError] = firstError;
    });
  }

  return firstError;
}


export function imageContentType(file) {
  const declaredType = String(file.type || "").trim().toLowerCase();
  if (ALLOWED_IMAGE_TYPES.has(declaredType)) return declaredType;
  return IMAGE_CONTENT_TYPES_BY_EXTENSION.get(fileExtension(file.name)) || declaredType;
}


export function imageOutputContentType(file) {
  const sourceType = imageContentType(file);
  if (sourceType === "image/png" || sourceType === "image/webp") return sourceType;
  return "image/jpeg";
}


export function processedImageFilename(originalName, contentType) {
  const extension = IMAGE_EXTENSIONS_BY_CONTENT_TYPE.get(contentType);
  if (!extension) return originalName;

  const currentExtension = fileExtension(originalName);
  const currentType = IMAGE_CONTENT_TYPES_BY_EXTENSION.get(currentExtension);
  if (currentType === contentType) return originalName;

  const basename = currentExtension
    ? originalName.slice(0, -currentExtension.length)
    : originalName;
  return `${basename || "photo"}${extension}`;
}


export async function decodeImageFile(file) {
  if (typeof window.createImageBitmap === "function") {
    try {
      const bitmap = await window.createImageBitmap(file, { imageOrientation: "from-image" });
      if (bitmap.width > 0 && bitmap.height > 0) {
        return {
          source: bitmap,
          width: bitmap.width,
          height: bitmap.height,
          close: () => bitmap.close(),
        };
      }
      bitmap.close();
    } catch {
      // Fall back to an image element for browsers with partial createImageBitmap support.
    }
  }

  return decodeImageElement(file);
}


export function decodeImageElement(file) {
  return new Promise((resolve, reject) => {
    let objectUrl;
    try {
      objectUrl = window.URL.createObjectURL(file);
    } catch (error) {
      reject(error);
      return;
    }

    const image = new window.Image();
    const releaseObjectUrl = () => window.URL.revokeObjectURL(objectUrl);
    image.onload = () => {
      releaseObjectUrl();
      if (image.naturalWidth <= 0 || image.naturalHeight <= 0) {
        reject(new Error("The image has no decodable dimensions."));
        return;
      }
      resolve({
        source: image,
        width: image.naturalWidth,
        height: image.naturalHeight,
        close() {},
      });
    };
    image.onerror = () => {
      releaseObjectUrl();
      reject(new Error("The browser could not decode the selected image."));
    };
    image.src = objectUrl;
  });
}


export async function encodeDecodedImage(decodedImage, width, height, contentType, quality) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  try {
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Canvas image processing is unavailable.");
    context.drawImage(decodedImage.source, 0, 0, width, height);

    return await new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob || blob.size === 0) {
            reject(new Error("The browser could not encode the selected image."));
            return;
          }
          resolve(blob);
        },
        contentType,
        quality
      );
    });
  } finally {
    canvas.width = 0;
    canvas.height = 0;
    canvas.remove();
  }
}


export async function tryNoResizeOptimization(file, decodedImage) {
  try {
    return await encodeDecodedImage(
      decodedImage,
      decodedImage.width,
      decodedImage.height,
      imageOutputContentType(file),
      NO_RESIZE_IMAGE_QUALITY
    );
  } catch {
    return null;
  }
}


export async function resizeImageUntilBelowLimit(file, decodedImage, initialComparisonSize) {
  const contentType = imageOutputContentType(file);
  let comparisonSize = initialComparisonSize;
  let scale = 1;
  let previousWidth = decodedImage.width;
  let previousHeight = decodedImage.height;
  let lastEncodingError = null;

  for (let attempt = 0; attempt < MAX_IMAGE_RESIZE_ATTEMPTS; attempt += 1) {
    const estimatedScale = Math.sqrt(MAX_IMAGE_BYTES / Math.max(1, comparisonSize)) * 0.95;
    scale *= Math.min(0.9, Math.max(0.5, estimatedScale));

    let width = Math.max(1, Math.floor(decodedImage.width * scale));
    let height = Math.max(1, Math.floor(decodedImage.height * scale));
    if (width === previousWidth && width > 1) width -= 1;
    if (height === previousHeight && height > 1) height -= 1;

    let blob;
    try {
      blob = await encodeDecodedImage(
        decodedImage,
        width,
        height,
        contentType,
        RESIZED_IMAGE_QUALITY
      );
    } catch (error) {
      lastEncodingError = error;
      if (width === 1 && height === 1) break;
      comparisonSize = Math.max(comparisonSize, MAX_IMAGE_BYTES * 4);
      previousWidth = width;
      previousHeight = height;
      continue;
    }
    if (blob.size <= MAX_IMAGE_BYTES) {
      return { blob, width, height };
    }

    if (width === 1 && height === 1) break;
    comparisonSize = blob.size;
    previousWidth = width;
    previousHeight = height;
  }

  throw lastEncodingError || new Error("The image could not be reduced below the attachment limit.");
}


export function fileFromProcessedBlob(originalFile, blob) {
  const contentType = String(blob.type || imageOutputContentType(originalFile)).toLowerCase();
  return new window.File(
    [blob],
    processedImageFilename(originalFile.name, contentType),
    {
      type: contentType,
      lastModified: originalFile.lastModified,
    }
  );
}


export async function reduceOversizedImage(file) {
  const decodedImage = await decodeImageFile(file);

  try {
    const optimizedBlob = await tryNoResizeOptimization(file, decodedImage);
    if (optimizedBlob && optimizedBlob.size <= MAX_IMAGE_BYTES) {
      return {
        file: fileFromProcessedBlob(file, optimizedBlob),
        resized: false,
      };
    }

    const resized = await resizeImageUntilBelowLimit(
      file,
      decodedImage,
      optimizedBlob?.size || file.size
    );
    return {
      file: fileFromProcessedBlob(file, resized.blob),
      resized: true,
      width: resized.width,
      height: resized.height,
    };
  } finally {
    decodedImage.close();
  }
}


export function photoReductionStatus(originalFile, processedImage) {
  const action = processedImage.resized
    ? copy("photo_status_resized")
    : copy("photo_status_optimized");
  return `${action} (${formatFileSize(originalFile.size)} to ${formatFileSize(processedImage.file.size)}).`;
}


export function photoExifLocationStatus(location) {
  if (!location) return copy("photo_exif_missing");
  return `${copy("photo_exif_found")}: ${location.latitude.toFixed(5)}, ${location.longitude.toFixed(5)}`;
}


export function setPhotoProcessingStatus(selection, status) {
  if (selection?.key) photoProcessingStatusesByKey.set(selection.key, status);
  if (selection?.file) photoProcessingStatuses.set(selection.file, status);
}


export function photoProcessingStatusForSelection(category, index, file) {
  const key = reportPhotoKeyForSelection(file, index, category);
  return photoProcessingStatusesByKey.get(key) || photoProcessingStatuses.get(file);
}


export function reportPhotoLocationResultForKey(key) {
  return photoExifLocationResults.get(key);
}


export function applyCurrentReportLocationSearchProgress(generation = state.reportLocation.generation) {
  const previousLocation = state.reportLocation;
  state.reportLocation = applyReportLocationSearchProgress(state.reportLocation, {
    generation,
    sources: currentReportLocationPhotoSources(),
    resultForKey: reportPhotoLocationResultForKey,
  });
  if (
    state.reportLocation !== previousLocation &&
    state.reportLocation.sourceType === REPORT_LOCATION_SOURCE.PHOTO
  ) {
    reportLocationInteractionGeneration += 1;
  }
}


export async function initializeReportLocationFromNonPhotoSources(generation) {
  let zipGeometry = null;
  try {
    zipGeometry = await loadArizonaZipGeometry();
  } catch {
    zipGeometry = null;
  }
  if (generation !== reportLocationInitializationGeneration) return;
  if (state.reportLocation.status !== REPORT_LOCATION_STATUS.SEARCHING) return;

  const candidate = initialReportLocationCoordinates({
    browserCoordinates: state.reportLocation.browserCoordinates,
    zipGeometry,
    zipCode: selectedExactLocationZip(),
  });
  if (!reportLocationHasValidStartingPoint(candidate)) return;
  state.reportLocation = initializeReportLocationPin(
    state.reportLocation,
    candidate.coordinates,
    candidate.sourceType,
    { sourceZip: candidate.sourceZip }
  );
  render();
}


export function ensureReportLocationInitialized() {
  if (!selectedReportLocationCategories().length) return;
  if ([
    REPORT_LOCATION_STATUS.AVAILABLE,
    REPORT_LOCATION_STATUS.CONFIRMED,
    REPORT_LOCATION_STATUS.MAP_ERROR,
  ].includes(state.reportLocation.status)) return;

  const sources = currentReportLocationPhotoSources();
  if (sources.length && state.reportLocation.status !== REPORT_LOCATION_STATUS.UNAVAILABLE) {
    const generation = startReportLocationDiscovery();
    if (generation) return;
    if (state.reportLocation.status === REPORT_LOCATION_STATUS.SEARCHING) return;
  }

  reportLocationInitializationGeneration += 1;
  state.reportLocation = {
    ...state.reportLocation,
    status: REPORT_LOCATION_STATUS.SEARCHING,
    sourceType: "",
    sourcePhotoKey: "",
    sourceCategory: "",
    sourceZip: "",
    markerCoordinates: null,
    confirmedCoordinates: null,
    mapError: "",
    generation: state.reportLocation.generation + 1,
  };
  void initializeReportLocationFromNonPhotoSources(reportLocationInitializationGeneration);
}


export function startReportLocationDiscovery({ force = false } = {}) {
  const sources = currentReportLocationPhotoSources();
  if (!sources.length) {
    return null;
  }
  if (!force && !canAutoDiscoverReportLocation(state.reportLocation)) return null;

  state.reportLocation = beginReportLocationSearch(state.reportLocation, sources);
  const generation = state.reportLocation.generation;
  sources.forEach((source) => {
    const result = photoExifLocationResults.get(source.key);
    if (!result || result.status === "pending") {
      photoExifLocationResults.set(source.key, {
        status: "pending",
        generation,
      });
    }
  });
  applyCurrentReportLocationSearchProgress(generation);
  return generation;
}


export function selectedReportPhotoKeys() {
  return new Set(currentReportLocationPhotoSources().map((source) => source.key));
}


export function handleReportPhotoRemoved(removedKey) {
  const previousStatus = state.reportLocation.status;
  const removedWasSource = state.reportLocation.sourcePhotoKey === removedKey;
  if (removedWasSource) reportLocationInteractionGeneration += 1;
  state.reportLocation = reportLocationSourceRemoved(state.reportLocation, removedKey);

  if (previousStatus === REPORT_LOCATION_STATUS.CONFIRMED) return;
  if (
    previousStatus === REPORT_LOCATION_STATUS.SEARCHING ||
    (previousStatus === REPORT_LOCATION_STATUS.AVAILABLE && removedWasSource)
  ) {
    startReportLocationDiscovery({ force: true });
  }
}


export async function updatePhotoExifLocationStatuses(selections) {
  const discoveryGeneration = canAutoDiscoverReportLocation(state.reportLocation)
    ? startReportLocationDiscovery()
    : null;
  const readGeneration = discoveryGeneration || state.reportLocation.generation;

  selections.forEach((selection) => {
    photoExifLocationResults.set(selection.key, {
      status: "pending",
      generation: readGeneration,
    });
    setPhotoProcessingStatus(selection, {
      kind: "exif",
      text: copy("photo_exif_reading"),
    });
  });
  render();

  await Promise.all(selections.map(async (selection) => {
    const { file, key } = selection;
    try {
      const location = await readPhotoGpsLocation(file);
      const resultGeneration = photoExifLocationResults.get(key)?.generation || readGeneration;
      photoExifLocationResults.set(key, {
        status: location ? "found" : "missing",
        coordinates: location || null,
        generation: resultGeneration,
      });
      setPhotoProcessingStatus(selection, {
        kind: location ? "exif" : "missing",
        text: photoExifLocationStatus(location),
      });
    } catch {
      const resultGeneration = photoExifLocationResults.get(key)?.generation || readGeneration;
      photoExifLocationResults.set(key, {
        status: "error",
        coordinates: null,
        generation: resultGeneration,
      });
      setPhotoProcessingStatus(selection, {
        kind: "error",
        text: copy("photo_exif_error"),
      });
    }
    const resultGeneration = photoExifLocationResults.get(key)?.generation || readGeneration;
    if (
      resultGeneration === state.reportLocation.generation &&
      selectedReportPhotoKeys().has(key)
    ) {
      applyCurrentReportLocationSearchProgress(resultGeneration);
      render();
    }
  }));
}


export function setPhotoErrors(message) {
  selectedPhotoCategories().forEach((category) => {
    const fields = observationFieldNames(category);
    if (state[fields.photos].length) state[fields.photoError] = message;
  });
}


export async function processOversizedPhotos() {
  const oversizedPhotos = selectedPhotoCategories().flatMap((category) => {
    const fields = observationFieldNames(category);
    return state[fields.photos]
      .map((file, index) => ({ category, fields, file, index }))
      .filter(({ file }) => file.size > MAX_IMAGE_BYTES);
  });

  if (oversizedPhotos.length === 0) return 0;

  oversizedPhotos.forEach(({ category, file, index }) => {
    setPhotoProcessingStatus({ file, key: reportPhotoKeyForSelection(file, index, category) }, {
      kind: "processing",
      text: copy("photo_status_processing"),
    });
  });
  setSubmissionStatus(
    `Preparing ${oversizedPhotos.length} oversized photo${oversizedPhotos.length === 1 ? "" : "s"} for submission.`,
    `Reducing ${oversizedPhotos.length} oversized photo${oversizedPhotos.length === 1 ? "" : "s"} client-side...`
  );
  render();

  let reducedCount = 0;
  for (const { category, fields, file, index } of oversizedPhotos) {
    try {
      const processedImage = await reduceOversizedImage(file);
      state[fields.photos][index] = processedImage.file;
      setPhotoProcessingStatus({ file: processedImage.file, key: reportPhotoKeyForSelection(file, index, category) }, {
        kind: "reduced",
        text: photoReductionStatus(file, processedImage),
      });
      reducedCount += 1;
    } catch {
      const message = `${file.name}: ${copy("photo_error_resize")}`;
      photoProcessingStatuses.set(file, { kind: "error", text: message });
      setPhotoErrors(message);
      throw new Error(message);
    }
  }

  render();
  return reducedCount;
}


export function attachmentSelectionsByRecord(records) {
  return records.reduce((attachments, record) => {
    const categories = Array.isArray(record.category) ? record.category : [];
    const selections = [];

    if (categories.includes("animals")) {
      state.animalPhotos.forEach((file) => selections.push({ category: "animals", file }));
    }
    if (categories.includes("environment")) {
      state.environmentPhotos.forEach((file) => selections.push({ category: "environment", file }));
    }

    if (selections.length) attachments[record.id] = selections;
    return attachments;
  }, {});
}


export function previewAttachmentsByCategory(selections) {
  return ["animals", "environment"].reduce((summary, category) => {
    const files = selections
      .filter((selection) => selection.category === category)
      .map((selection) => selection.file);
    if (files.length) {
      summary[category] = {
        attachment_count: files.length,
        total_size_bytes: totalPhotoBytes(files),
      };
    }
    return summary;
  }, {});
}


export function previewPayload(records, attachmentSelections) {
  if (Object.keys(attachmentSelections).length === 0) {
    return records.length === 1 ? records[0] : records;
  }

  return {
    records,
    pending_attachment_uploads: Object.fromEntries(
      Object.entries(attachmentSelections).map(([recordId, selections]) => [
        recordId,
        {
          attachment_count: selections.length,
          total_size_bytes: totalPhotoBytes(selections.map((selection) => selection.file)),
          by_category: previewAttachmentsByCategory(selections),
        },
      ])
    ),
  };
}


export function attachmentContentType(file) {
  return String(file.type || "").trim().toLowerCase();
}


export async function createAttachmentUpload(recordId, { category, file }) {
  const response = await fetch(apiUrl("/community-reports/attachments/uploads"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      report_id: recordId,
      category,
      filename: file.name,
      content_type: attachmentContentType(file),
      size_bytes: file.size,
    }),
  });
  if (!response.ok) {
    throw new Error(`Attachment upload endpoint returned HTTP ${response.status}`);
  }
  const result = await response.json();
  if (result.error) {
    throw new Error(result.error);
  }
  return result;
}


export async function uploadAttachmentFile(upload, file) {
  for (let attempt = 0; attempt <= MAX_ATTACHMENT_UPLOAD_RETRIES; attempt += 1) {
    const response = await fetch(upload.url, {
      method: upload.method || "PUT",
      headers: upload.headers || {
        "Content-Type": attachmentContentType(file),
      },
      body: file,
    });
    if (response.ok) return;
    const retryAfterSeconds = Number(response.headers.get("Retry-After"));
    const canRetry = (response.status === 429 || response.status === 503) && attempt < MAX_ATTACHMENT_UPLOAD_RETRIES;
    if (!canRetry) {
      throw new Error(`Attachment upload returned HTTP ${response.status}`);
    }
    await new Promise((resolve) => window.setTimeout(resolve, Math.max(1, retryAfterSeconds || 1) * 1000));
  }
}


export async function uploadAttachmentsForRecords(records) {
  const attachmentSelections = attachmentSelectionsByRecord(records);
  if (Object.keys(attachmentSelections).length === 0) return records;

  const uploadedAttachmentsByRecord = Object.fromEntries(
    await Promise.all(
      Object.entries(attachmentSelections).map(async ([recordId, selections]) => {
        const attachments = await Promise.all(
          selections.map(async (selection) => {
            const result = await createAttachmentUpload(recordId, selection);
            await uploadAttachmentFile(result.upload, selection.file);
            return result.attachment;
          })
        );
        return [recordId, attachments];
      })
    )
  );

  return records.map((record) => {
    const attachments = uploadedAttachmentsByRecord[record.id];
    return attachments?.length ? { ...record, attachments } : record;
  });
}


export function renderPhotoPicker(category, fields) {
  const files = state[fields.photos];
  const photoError = state[fields.photoError];
  return `
    <div class="photo-picker" aria-busy="${isSubmitting}">
      <input
        id="${fields.photoInputId}"
        class="file-input"
        type="file"
        accept="${PHOTO_INPUT_ACCEPT}"
        multiple
        data-photo-category="${category}"
        ${isSubmitting ? "disabled" : ""}
        hidden
      >
      <button class="photo-button" type="button" data-action="photo" ${isSubmitting ? "disabled" : ""}>
        ${files.length ? copy("photo_add_more") : copy("photo_add")}
      </button>
      ${files.length ? `<p class="photo-summary">${photoSummaryText(files)}</p>` : ""}
      ${files.length ? `
        <div class="photo-list" aria-label="${copy("photo_h")}">
          ${files.map((file, index) => {
            const status = photoProcessingStatusForSelection(category, index, file);
            return `
              <div class="photo-row">
                <span class="photo-name">${escapeHtml(file.name)}</span>
                <span class="photo-meta">${formatFileSize(file.size)}</span>
                <button
                  class="photo-remove-button"
                  type="button"
                  data-action="remove-photo"
                  data-photo-category="${category}"
                  data-photo-index="${index}"
                  ${isSubmitting ? "disabled" : ""}
                >
                  ${copy("photo_remove")}
                </button>
                ${status ? `
                  <span class="photo-status ${status.kind === "error" ? "error" : ""}">
                    ${escapeHtml(status.text)}
                  </span>
                ` : ""}
              </div>
            `;
          }).join("")}
        </div>
      ` : ""}
      ${photoError ? `<p class="field-error">${escapeHtml(photoError)}</p>` : ""}
    </div>
  `;
}


export function currentStep() {
  return steps()[state.step] || "done";
}


export function render() {
  document.body.classList.toggle("calendar-modal-open", Boolean(state.datePickerTarget));
  const name = currentStep();
  cleanupPhotoLocationMap();
  // Restore the action bar to its footer home before any step rebuilds #screen.
  // On narrow front-door layouts it gets relocated into #screen above the map, and
  // an innerHTML reset there would otherwise destroy the shared #nextButton element.
  if (screen.contains(actionBar)) actionBarHome.appendChild(actionBar);
  backButton.classList.toggle("visible", state.step > 0 && name !== "done" && name !== POST_SUBMIT_REGISTRATION_STEP);
  backButton.disabled = isSubmitting;
  actionBar.hidden = name === "done" || name === "peopleSignIn" || name === POST_SUBMIT_REGISTRATION_STEP;
  renderProgress();

  if (name === "frontDoor") renderFrontDoor();
  if (name === "peopleSubject") renderPeopleSubject();
  if (name === "peopleSignIn") renderPeopleSignIn();
  if (isPeopleFeelingStep(name)) renderPeopleFeeling(peopleSubjectForStep(name));
  if (isPeopleGoodCheckInStep(name)) renderPeopleGoodCheckIn(peopleSubjectForStep(name));
  if (isPeopleSymptomsStep(name)) renderPeopleSymptoms(peopleSubjectForStep(name));
  if (isPeopleAssessmentStep(name)) renderPeopleAssessment(peopleSubjectForStep(name));
  if (isPeopleExposuresStep(name)) renderPeopleExposures(peopleSubjectForStep(name));
  if (isPeopleTravelStep(name)) renderPeopleTravel(peopleSubjectForStep(name));
  if (name === POST_SUBMIT_REGISTRATION_STEP) renderPostSubmitRegistration();
  if (name === "animalCategory") renderAnimalCategory();
  if (name === "animalSpecies") renderAnimalSpecies();
  if (name === "animalCondition") renderAnimalCondition();
  if (name === "animalCount") renderAnimalCount();
  if (name === "animalDate") renderAnimalDate();
  if (name === "animalLocation") renderAnimalLocation();
  if (name === "animalReporterFeeling") renderAnimalReporterFeeling();
  if (name === "environmentIssue") renderEnvironmentIssue();
  if (name === "environmentConcern") renderEnvironmentConcern();
  if (name === "environmentLocation") renderEnvironmentLocation();
  if (name === "environmentDate") renderEnvironmentDate();
  if (name === "environmentReporterFeeling") renderEnvironmentReporterFeeling();
  if (name === "reportLocation") renderReportLocation();
  if (name === "done") renderDone();
  screen.insertAdjacentHTML("beforeend", renderDatePickerModal());

  renderCommunityReportCount();
  nextButton.textContent = name === "frontDoor" || !isSubmitStep() ? t().continue : t().submit;
  nextButton.disabled = isSubmitting || !canContinue();
  renderSettings();
  renderReportMaps();
  renderReportLocationMap();
  renderCommunityPulse();

  // Front door: keep the Continue action directly above the map panel.
  if (name === "frontDoor") {
    const mapPanel = screen.querySelector(".front-door-map-panel");
    if (mapPanel) screen.insertBefore(actionBar, mapPanel);
  }
}


export function renderProgress() {
  progressDots.innerHTML = steps()
    .map((_, index) => `<span class="dot ${index === state.step ? "active" : ""}"></span>`)
    .join("");
}


export function renderFrontDoor() {
  trackScreenView("front-door");
  screen.innerHTML = `
    <h2>${copy("front_door_h")}</h2>
    <p class="lede">${t().cat_s}</p>
    <div class="category-list front-door-category-list">
      ${categoryRow("people", t().people, t().people_d, "P")}
      ${categoryRow("animals", t().animals, t().animals_d, "A")}
      ${categoryRow("environment", t().env, t().env_d, "E")}
    </div>
    <section class="front-door-map-panel" aria-label="${copy("map_aria_zip_report")}">
      <div class="map-heading">
        <div>
          <p class="eyebrow">${copy("map_arizona_zip_codes")}</p>
          <h3>${copy("map_live_report")}</h3>
        </div>
        <span class="map-count" data-mobile-map-count>${countCopy("map_report_one", "map_report_many", 0)}</span>
      </div>
      <div class="front-door-map-layout">
        <div class="report-map front-door-map compact" id="frontDoorReportMap"></div>
        <div class="front-door-map-rail">
          <div class="map-lab-section map-lab-zip-search">
            <h4 class="map-lab-section-title">${copy("map_find_zip")}</h4>
            <div class="zip-search-row">
              <input type="text" inputmode="numeric" pattern="[0-9]*" maxlength="5" class="zip-search-input" id="zipSearchInput" data-zip-search-input placeholder="${copy("map_zip_example")}" aria-label="${copy("map_search_zip_aria")}" autocomplete="postal-code" />
              <button type="button" class="zip-search-button" data-action="zip-search">${copy("map_go")}</button>
            </div>
            <p class="zip-search-status" data-zip-search-status role="status" aria-live="polite"></p>
          </div>
          <div class="map-lab-section">
            <h4 class="map-lab-section-title">${copy("map_layers")}</h4>
            ${mapLabLayerToggleMarkup()}
          </div>
          <div class="map-lab-section">
            <h4 class="map-lab-section-title">${copy("map_people_reports")}</h4>
            ${mapLabSymptomFilterMarkup()}
          </div>
          <div class="map-lab-section">
            <h4 class="map-lab-section-title">${copy("map_time_window")}</h4>
            <div data-report-map-time-controls>${reportMapTimeControlsMarkup()}</div>
          </div>
        </div>
      </div>
      ${mapZoomDebugMarkup("frontDoorReportMap")}
      ${reportDotLegendMarkup()}
      <p class="map-note" data-mobile-map-note>${copy("map_waiting_live")}</p>
    </section>
    <div class="community-pulse-slot"></div>
  `;
}


function mapZoomDebugMarkup(mapId) {
  if (!DEBUG_MODE) return "";
  return `
    <div class="map-debug map-zoom-debug" data-map-debug-zoom-for="${mapId}">
      <strong>Debug</strong>
      <span>Zoom: <span data-map-debug-zoom>--</span></span>
    </div>
  `;
}


// Front-door ZIP search: read the rail input, zoom the map + open the ZIP popup, and echo
// any validation/not-found message into the inline status line.
async function runZipSearch() {
  const input = document.querySelector("[data-zip-search-input]");
  const status = document.querySelector("[data-zip-search-status]");
  if (!input) return;
  const result = await searchZipOnMap(input.value);
  if (result.ok) input.value = result.zipCode;
  if (status) {
    status.textContent = result.ok ? "" : result.message;
    status.classList.toggle("error", !result.ok);
  }
}


export function renderPeopleFeeling(subject) {
  trackScreenView(`people-${subject}-feeling`);
  screen.innerHTML = `
    <p class="field-label">${peopleReportFlowLabel(subject)}</p>
    <h2>${copy("people_feeling_h")}</h2>
    <p class="lede">${t().feeling_s}</p>
    <div class="two-choice">
      ${optionCard("sick", t().sick, t().sick_d, "S")}
      ${optionCard("good", t().good, t().good_d, "G")}
    </div>
  `;
}


export function renderPeopleSubject() {
  trackScreenView("people-subject");
  screen.innerHTML = `
    <p class="field-label">${reportFlowLabel("people")}</p>
    <h2>${copy("subject_h")}</h2>
    <p class="lede">${copy("subject_s")}</p>
    <div class="two-choice">
      ${subjectCard("self", copy("subject_self"), copy("subject_self_d"), "M")}
      ${subjectCard("other", copy("subject_other"), copy("subject_other_d"), "O")}
    </div>
  `;
}


export function renderPeopleSignIn() {
  trackScreenView("people-sign-in");
  screen.innerHTML = `
    <p class="field-label">${peopleReportFlowLabel("other")}</p>
    <h2>${copy("subject_signin_h")}</h2>
    <p class="lede">${copy("subject_signin_s")}</p>
    <button class="primary-button" type="button" data-action="register-and-continue">${copy("subject_signin_cta")}</button>
  `;
}


export function renderPeopleGoodCheckIn(subject) {
  trackScreenView(`people-${subject}-good-check-in`);
  screen.innerHTML = `
    <p class="field-label">${peopleReportFlowLabel(subject)}</p>
    <h2>${copy("people_good_h")}</h2>
    <p class="lede">${copy("people_good_s")}</p>
    ${peopleZipPrompt(subject)}
  `;
}


export function renderPostSubmitRegistration() {
  trackScreenView("post-submit-registration");
  screen.innerHTML = `
    <div class="done-view">
      <div class="done-icon">✓</div>
      <h2>${copy("post_submit_register_h")}</h2>
      <p class="lede">${copy("post_submit_register_s")}</p>
      <button class="primary-button" type="button" data-action="register-account">${copy("post_submit_register_yes")}</button>
      <button class="secondary-button" type="button" data-action="skip-registration">${copy("post_submit_register_no")}</button>
    </div>
  `;
}


export function renderPeopleSymptoms(subject) {
  const report = state.peopleReports[subject];
  trackScreenView(`people-${subject}-symptoms`);
  screen.innerHTML = `
    <p class="field-label">${peopleReportFlowLabel(subject)}</p>
    <h2>${copy("people_report_h")}</h2>
    <p class="lede">${copy("people_report_s")}</p>
    <div class="chip-grid symptom-grid">${SYMPTOMS.map((item) => symptomChip(item, report.symptoms.includes(item))).join("")}</div>
    ${report.symptoms.includes("Other") ? textInput("otherSymptom", t().desc_sym, report.otherSymptom) : ""}
    <p class="field-label">${t().how_many}</p>
    ${numberInput("sickCount", report.sickCount, "1", PEOPLE_SICK_COUNT_MAX_LENGTH)}
    ${peopleZipPrompt(subject)}
  `;
}


export function renderPeopleAssessment(subject) {
  const report = state.peopleReports[subject];
  trackScreenView(`people-${subject}-assessment`);
  screen.innerHTML = `
    <p class="field-label">${peopleReportFlowLabel(subject)}</p>
    <h2>${t().more_h}</h2>
    <p class="lede">${t().more_s}</p>
    <p class="field-label">${t().when_start}</p>
    ${dateInput(report.onset, peopleDateTarget(subject))}
    <p class="field-label">${t().prof_diag}</p>
    <div class="chip-grid">
      ${["Yes", "No", "Haven't seen a doctor"].map((item) => chip(item, report.diagnosed === item, "diagnosed", item === "Haven't seen a doctor" ? t().no_doc : item)).join("")}
    </div>
    ${report.diagnosed === "Yes" ? `<p class="field-label">${t().what_diag}</p><div class="chip-grid">${DIAGNOSES.map((item) => chip(item, report.diagnosis === item, "diagnosis")).join("")}</div>` : ""}
    <p class="field-label">${t().called_in_sick}</p>
    <div class="chip-grid">${["Yes", "No", "N/A"].map((item) => chip(item, report.calledInSick === item, "calledInSick")).join("")}</div>
  `;
}

export function renderPeopleExposures(subject) {
  const report = state.peopleReports[subject];
  const exposures = report.exposures;
  trackScreenView(`people-${subject}-exposures`);
  screen.innerHTML = `
    <p class="field-label">${peopleReportFlowLabel(subject)}</p>
    <h2>${copy("exposures_h")}</h2>
    <p class="lede">${copy("exposures_s")}</p>
    <div class="exposure-list">
      ${exposureQuestion(copy("exposure_mass_gathering"), "massGathering", exposures.massGathering)}
      ${exposures.massGathering === "Yes" ? `
        <div class="exposure-follow-up">
          <p class="field-label">${copy("exposure_event")}</p>
          <input class="text-area" data-input="massGatheringEvent" value="${escapeHtml(exposures.massGatheringEvent)}">
        </div>
      ` : ""}
      ${exposureQuestion(copy("exposure_live_animal"), "liveAnimalContact", exposures.liveAnimalContact)}
      ${exposures.liveAnimalContact === "Yes" ? `
        <div class="exposure-follow-up">
          <p class="field-label">${copy("exposure_bitten")}</p>
          ${yesNoChips("liveAnimalBitten", exposures.liveAnimalBitten)}
          <p class="field-label">${copy("exposure_animal_sick")}</p>
          ${yesNoChips("liveAnimalAppearedSick", exposures.liveAnimalAppearedSick)}
        </div>
      ` : ""}
      ${exposureQuestion(copy("exposure_dead_animal"), "deadAnimalContact", exposures.deadAnimalContact)}
      ${exposureQuestion(copy("exposure_sick_individual"), "sickIndividualContact", exposures.sickIndividualContact)}
      ${exposureQuestion(copy("exposure_tick_insect"), "tickOrInsectBite", exposures.tickOrInsectBite)}
    </div>
  `;
}

export function renderPeopleTravel(subject) {
  const report = state.peopleReports[subject];
  const exposures = report.exposures;
  trackScreenView(`people-${subject}-travel`);
  screen.innerHTML = `
    <p class="field-label">${peopleReportFlowLabel(subject)}</p>
    <h2>${copy("travel_h")}</h2>
    <p class="lede">${copy("travel_s")}</p>
    <div class="exposure-list">
      ${exposureQuestion(copy("travel_question"), "recentTravel", exposures.recentTravel)}
      ${exposures.recentTravel === "Yes" ? `
        <div class="exposure-follow-up">
          <p class="field-label">${copy("travel_location")}</p>
          ${travelLocationAutocompleteMarkup(subject, exposures)}
        </div>
      ` : ""}
    </div>
  `;
}

export function travelLocationAutocompleteMarkup(subject, exposures) {
  const autocomplete = state.travelAutocomplete;
  const isActiveSubject = autocomplete.subject === subject;
  return `
    <div class="location-autocomplete" data-travel-autocomplete="${subject}">
      <input
        class="text-area location-autocomplete-input"
        data-input="recentTravelLocation"
        value="${escapeHtml(exposures.recentTravelLocation)}"
        placeholder="${escapeHtml(copy("travel_location_placeholder"))}"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded="${isActiveSubject && autocomplete.suggestions.length > 0 ? "true" : "false"}"
        aria-controls="travelLocationSuggestions"
      >
      <div id="travelLocationSuggestions" data-travel-location-results>
        ${isActiveSubject ? travelLocationAutocompleteResultsMarkup(autocomplete, exposures) : ""}
      </div>
    </div>
  `;
}

export function travelLocationAutocompleteResultsMarkup(autocomplete, exposures = currentPeopleReport().exposures) {
  if (autocomplete.status === "loading") {
    return `<p class="field-status autocomplete-status">${copy("travel_location_searching")}</p>`;
  }
  if (autocomplete.status === "empty") {
    return `<p class="field-status autocomplete-status">${copy("travel_location_empty")}</p>`;
  }
  if (autocomplete.status === "error") {
    return `<p class="field-status autocomplete-status">${copy("travel_location_error")}</p>`;
  }
  if (exposures.recentTravelLocationSelection) {
    return `<p class="field-status autocomplete-status">${copy("travel_location_selected")}</p>`;
  }
  if (autocomplete.status !== "ready" || autocomplete.suggestions.length === 0) return "";
  return `
    <div class="location-autocomplete-list" role="listbox">
      ${autocomplete.suggestions.map((suggestion, index) => `
        <button
          type="button"
          class="location-autocomplete-option"
          data-travel-location-suggestion="${index}"
          role="option"
        >${escapeHtml(suggestion.label)}</button>
      `).join("")}
    </div>
  `;
}

function resetTravelLocationAutocomplete() {
  state.travelAutocomplete = emptyLocationAutocompleteState();
}

function updateTravelLocationAutocompleteResults() {
  const results = screen.querySelector("[data-travel-location-results]");
  if (!results) return;
  const report = currentPeopleReport();
  results.innerHTML = travelLocationAutocompleteResultsMarkup(state.travelAutocomplete, report.exposures);
  const input = screen.querySelector("[data-input='recentTravelLocation']");
  if (input) input.setAttribute("aria-expanded", String(state.travelAutocomplete.suggestions.length > 0));
  nextButton.disabled = isSubmitting || !canContinue();
}

async function updateRecentTravelLocationAutocomplete(subject, query) {
  const trimmedQuery = query.trim();
  state.travelAutocomplete = {
    ...emptyLocationAutocompleteState(trimmedQuery),
    subject,
    status: trimmedQuery.length >= 2 ? "loading" : "idle",
  };
  updateTravelLocationAutocompleteResults();
  const result = await travelLocationSearch(trimmedQuery);
  const report = state.peopleReports[subject];
  if (!isPeopleTravelStep() || peopleSubjectForStep() !== subject || report.exposures.recentTravelLocation.trim() !== trimmedQuery) return;
  if (result.status === "stale") return;
  report.exposures.recentTravelLocationFallbackAllowed = ["empty", "error"].includes(result.status);
  state.travelAutocomplete = { ...result, subject };
  updateTravelLocationAutocompleteResults();
}


export function renderAnimalCategory() {
  trackScreenView("animals-category");
  screen.innerHTML = `
    <p class="field-label">${reportFlowLabel("animals")}</p>
    <h2>${copy("animal_category_h")}</h2>
    <p class="lede">${copy("animal_category_s")}</p>
    <div class="two-choice">
      ${ANIMAL_GROUP_OPTIONS.map(([value, label]) => animalOptionCard(value, label, value === "livestock" ? copy("animal_category_livestock_d") : copy("animal_category_wildlife_d"), value === "livestock" ? "D" : "W", "animalGroup", state.animalGroup === value)).join("")}
    </div>
  `;
}


export function renderAnimalSpecies() {
  const options = animalSpeciesOptions(state.animalGroup);
  trackScreenView("animals-species");
  screen.innerHTML = `
    <p class="field-label">${reportFlowLabel("animals")}</p>
    <h2>${copy("animal_species_h")}</h2>
    <p class="lede">${copy("animal_species_s")}</p>
    <div class="chip-grid">${options.map(({ label }) => chip(label, state.animalSpecies === label, "animalSpecies")).join("")}</div>
  `;
}


export function renderAnimalCondition() {
  const options = animalStatusOptions(state.animalGroup);
  trackScreenView("animals-condition");
  screen.innerHTML = `
    <p class="field-label">${reportFlowLabel("animals")}</p>
    <h2>${copy("animal_condition_h")}</h2>
    <p class="lede">${state.animalGroup === "wildlife" ? copy("animal_condition_wildlife_s") : copy("animal_condition_s")}</p>
    <div class="two-choice">
      ${options.map(([value, label]) => animalOptionCard(value, label, value === "sick" ? copy("animal_condition_sick_d") : copy("animal_condition_dead_d"), value === "sick" ? "S" : "D", "animalStatus", state.animalStatus === value)).join("")}
    </div>
  `;
}


export function renderAnimalCount() {
  const countField = activeAnimalCountField(state.animalStatus) || "animalSickCount";
  const countLabel = state.animalStatus === "dead" ? copy("animal_dead_count_question") : copy("animal_sick_count_question");
  trackScreenView("animals-count");
  screen.innerHTML = `
    <p class="field-label">${reportFlowLabel("animals")}</p>
    <h2>${copy("animal_count_h")}</h2>
    <p class="lede">${copy("animal_count_s")}</p>
    <p class="field-label">${countLabel}</p>
    ${numberInput(countField, state[countField], "1", ANIMAL_COUNT_MAX_LENGTH)}
  `;
}


export function renderAnimalDate() {
  trackScreenView("animals-date");
  const hasOutOfRangeDate = state.animalObservedDate && !isAnimalFoundDateInRange(state.animalObservedDate);
  screen.innerHTML = `
    <p class="field-label">${reportFlowLabel("animals")}</p>
    <h2>${copy("animal_date_h")}</h2>
    <p class="lede">${copy("animal_date_s")}</p>
    <p class="field-label">${copy("observed_date")}</p>
    ${dateInput(state.animalObservedDate, "animals")}
    ${hasOutOfRangeDate ? `<p class="field-error">${copy("animal_date_error")}</p>` : ""}
  `;
}


export function renderAnimalLocation() {
  const fields = observationFieldNames("animals");
  trackScreenView("animals-location");
  screen.innerHTML = `
    <p class="field-label">${reportFlowLabel("animals")}</p>
    <h2>${copy("animal_location_h")}</h2>
    <p class="lede">${copy("animal_location_s")}</p>
    <p class="field-label">${copy("report_zip_h")}</p>
    ${zipInput(fields.zip)}
    <p class="field-label">${copy("photo_h")}</p>
    ${renderPhotoPicker("animals", fields)}
    <p class="field-label">${t().notes}</p>
    <textarea class="text-area" data-input="${fields.notes}" placeholder="${t().any_det}">${state[fields.notes]}</textarea>
  `;
}


export function renderAnimalReporterFeeling() {
  trackScreenView("animals-reporter-feeling");
  screen.innerHTML = `
    <p class="field-label">${reportFlowLabel("animals")}</p>
    <h2>${copy("animal_reporter_feeling_h")}</h2>
    <p class="lede">${copy("animal_reporter_feeling_s")}</p>
    <div class="chip-grid">
      ${ANIMAL_REPORTER_FEELING_OPTIONS.map(([value]) => chip(value, state.animalReporterFeeling === value, "animalReporterFeeling", copy(`animal_reporter_feeling_${value}`))).join("")}
    </div>
  `;
}


export function renderEnvironmentIssue() {
  trackScreenView("environment-issue");
  screen.innerHTML = `
    <p class="field-label">${reportFlowLabel("environment")}</p>
    <h2>${copy("environment_issue_h")}</h2>
    <p class="lede">${copy("environment_issue_s")}</p>
    <div class="two-choice">
      ${ENVIRONMENT_ISSUE_OPTIONS.map(([value]) => animalOptionCard(
        value,
        value === "insect_vectors" ? copy("environment_issue_insect_vectors") : copy("environment_issue_environmental_concern"),
        value === "insect_vectors" ? copy("environment_issue_insect_vectors_d") : copy("environment_issue_environmental_concern_d"),
        value === "insect_vectors" ? "I" : "E",
        "environmentIssueType",
        state.environmentIssueType === value
      )).join("")}
    </div>
  `;
}


export function renderEnvironmentConcern() {
  const isInsectVectorReport = state.environmentIssueType === "insect_vectors";
  trackScreenView("environment-concern");
  screen.innerHTML = `
    <p class="field-label">${reportFlowLabel("environment")}</p>
    <h2>${copy("environment_concern_h")}</h2>
    <p class="lede">${isInsectVectorReport ? copy("environment_insect_concern_s") : copy("environment_concern_s")}</p>
    <div class="chip-grid">
      ${isInsectVectorReport
        ? INSECT_VECTOR_OPTIONS.map(([value, label]) => chip(value, state.insectVectorType === value, "insectVectorType", label)).join("")
        : ENVIRONMENTAL_CONCERN_OPTIONS.map((item) => chip(item, state.environmentConcerns.includes(item), "environmentConcern")).join("")}
    </div>
  `;
}


export function renderEnvironmentLocation() {
  const fields = observationFieldNames("environment");
  trackScreenView("environment-location");
  screen.innerHTML = `
    <p class="field-label">${reportFlowLabel("environment")}</p>
    <h2>${copy("environment_location_h")}</h2>
    <p class="lede">${copy("environment_location_s")}</p>
    <p class="field-label">${copy("report_zip_h")}</p>
    ${zipInput(fields.zip)}
    <p class="field-label">${copy("photo_h")}</p>
    ${renderPhotoPicker("environment", fields)}
    <p class="field-label">${t().notes}</p>
    <textarea class="text-area" data-input="${fields.notes}" placeholder="${t().any_det}">${state[fields.notes]}</textarea>
  `;
}


export function renderEnvironmentDate() {
  trackScreenView("environment-date");
  const hasOutOfRangeDate = state.environmentObservedDate && !isEnvironmentObservedDateInRange(state.environmentObservedDate);
  screen.innerHTML = `
    <p class="field-label">${reportFlowLabel("environment")}</p>
    <h2>${copy("environment_date_h")}</h2>
    <p class="lede">${copy("environment_date_s")}</p>
    <p class="field-label">${copy("observed_date")}</p>
    ${dateInput(state.environmentObservedDate, "environment")}
    ${hasOutOfRangeDate ? `<p class="field-error">${copy("environment_date_error")}</p>` : ""}
  `;
}


export function renderEnvironmentReporterFeeling() {
  trackScreenView("environment-reporter-feeling");
  screen.innerHTML = `
    <p class="field-label">${reportFlowLabel("environment")}</p>
    <h2>${copy("environment_reporter_feeling_h")}</h2>
    <p class="lede">${copy("environment_reporter_feeling_s")}</p>
    <div class="chip-grid">
      ${ENVIRONMENT_REPORTER_FEELING_OPTIONS.map(([value]) => chip(value, state.environmentReporterFeeling === value, "environmentReporterFeeling", copy(`environment_reporter_feeling_${value}`))).join("")}
    </div>
  `;
}


export function renderObservations(category) {
  const fields = observationFieldNames(category);
  const observations = state[fields.observations];
  const reportTitle = category === "animals" ? copy("animal_report_h") : copy("environment_report_h");
  const reportSubtitle = category === "animals" ? copy("animal_report_s") : copy("environment_report_s");

  trackScreenView(`${category}-observations`);

  screen.innerHTML = `
    <p class="field-label">${reportFlowLabel(category)}</p>
    <h2>${reportTitle}</h2>
    <p class="lede">${reportSubtitle}</p>
    <div class="chip-grid">${observationOptions(category).map((item) => chip(item, observations.includes(item), "observation")).join("")}</div>
    <p class="field-label">${copy("observed_date")}</p>
    ${dateInput(state[fields.date], category)}
    <p class="field-label">${t().loc}</p>
    ${zipInput(fields.zip)}
    <p class="field-label">${copy("photo_h")}</p>
    ${renderPhotoPicker(category, fields)}
    <p class="field-label">${t().notes}</p>
    <textarea class="text-area" data-input="${fields.notes}" placeholder="${t().any_det}">${state[fields.notes]}</textarea>
  `;
}


export function reportLocationStatusText() {
  if (state.reportLocation.status === REPORT_LOCATION_STATUS.SEARCHING) return copy("report_location_searching");
  if (state.reportLocation.status === REPORT_LOCATION_STATUS.UNAVAILABLE) return copy("report_location_unavailable");
  if (state.reportLocation.status === REPORT_LOCATION_STATUS.AVAILABLE) return copy("report_location_available");
  if (state.reportLocation.status === REPORT_LOCATION_STATUS.CONFIRMED) return copy("report_location_confirmed");
  if (state.reportLocation.status === REPORT_LOCATION_STATUS.DECLINED) return copy("report_location_declined");
  if (state.reportLocation.status === REPORT_LOCATION_STATUS.MAP_ERROR) return copy("report_location_map_error");
  return copy("report_location_unavailable");
}


export function reportLocationCoordinatesMarkup() {
  if (![
    REPORT_LOCATION_STATUS.AVAILABLE,
    REPORT_LOCATION_STATUS.CONFIRMED,
  ].includes(state.reportLocation.status)) return "";
  const coordinates = state.reportLocation.markerCoordinates;
  if (!isValidCoordinatePair(coordinates)) return "";
  return `
    <p class="report-location-coordinates">
      <span>${copy("report_location_coords")}</span>
      <strong>${formatReportCoordinates(coordinates)}</strong>
    </p>
  `;
}


export function renderReportLocationControls() {
  const status = state.reportLocation.status;
  if (status === REPORT_LOCATION_STATUS.AVAILABLE) {
    return `
      <button class="primary-button" type="button" data-action="confirm-report-location">
        ${copy("report_location_confirm")}
      </button>
    `;
  }
  return "";
}


export function renderReportLocation() {
  trackScreenView("report-location");
  ensureReportLocationInitialized();
  const showMap = [
    REPORT_LOCATION_STATUS.AVAILABLE,
    REPORT_LOCATION_STATUS.CONFIRMED,
  ].includes(state.reportLocation.status) && isValidCoordinatePair(state.reportLocation.markerCoordinates);
  screen.innerHTML = `
    <section class="report-location-section" aria-labelledby="reportLocationTitle">
      <p class="field-label">${copy("report_location_h")}</p>
      <h2 id="reportLocationTitle">${copy("report_location_h")}</h2>
      <p class="lede">${copy("report_location_s")}</p>
      <p class="field-status report-location-status" aria-live="polite">
        ${escapeHtml(reportLocationStatusText())}
      </p>
      ${showMap ? `<div class="report-location-map" id="reportLocationMap" aria-label="${copy("report_location_h")}"></div>` : ""}
      ${reportLocationCoordinatesMarkup()}
      <div class="report-location-actions">
        ${renderReportLocationControls()}
      </div>
    </section>
  `;
}


export function renderReportLocationMap() {
  const container = document.getElementById("reportLocationMap");
  if (!container) return;
  const coordinates = state.reportLocation.markerCoordinates;
  if (!isValidCoordinatePair(coordinates)) return;
  initializePhotoLocationMap({
    container,
    coordinates,
    copy,
    onDragEnd: (nextCoordinates) => {
      reportLocationInteractionGeneration += 1;
      state.reportLocation = moveReportLocationMarker(state.reportLocation, nextCoordinates);
      render();
    },
    onError: (message) => {
      reportLocationInteractionGeneration += 1;
      state.reportLocation = reportLocationMapFailed(state.reportLocation, message);
      render();
    },
  });
}


export function renderDone() {
  trackScreenView("done");
  screen.innerHTML = `
    <div class="done-view">
      <div class="done-icon">✓</div>
      <h2>${t().thanks}</h2>
      <div class="nearby-card">
        <div class="metric-pair">
          <span class="metric">${communityTotalReportCountText()}</span>
          <span>${t().total_reports}<br>${t().pima}</span>
        </div>
      </div>
      <section class="mobile-map-panel" aria-label="${copy("map_aria_live_report_count")}">
        <div class="map-heading">
          <div>
            <p class="eyebrow">${copy("map_arizona_zip_codes")}</p>
            <h3>${copy("map_live_report_count")}</h3>
          </div>
          <span class="map-count" data-mobile-map-count>${countCopy("map_report_one", "map_report_many", 0)}</span>
        </div>
        <div class="report-map compact" id="mobileReportMap"></div>
        ${reportDotLegendMarkup()}
        <p class="map-note" data-mobile-map-note>${copy("map_waiting_live")}</p>
      </section>
      <div class="community-pulse-slot"></div>
      <button class="primary-button" type="button" data-action="reset">${t().rep_anon}</button>
    </div>
  `;
}


export function renderCommunityReportCount() {
  communityTotalReportCount.textContent = communityTotalReportCountText();
  communityTotalReportCountLabel.textContent = `${t().total_reports} ${t().pima} ${reportMapTimePeriodText()}`;
}


export function communityTotalReportCountText() {
  return Number.isInteger(state.communityTotalReportCount) ? String(state.communityTotalReportCount) : "...";
}


export function renderReportMapTimeControls() {
  const markup = reportMapTimeControlsMarkup();
  document.querySelectorAll("[data-report-map-time-controls]").forEach((node) => {
    node.innerHTML = markup;
  });
}


export function reportMapTimeControlsMarkup() {
  const selectedDate = state.mapTimeSelectedDate;
  const windowStartDate = state.mapTimeWindowStartDate;
  const windowEndDate = state.mapTimeWindowEndDate;
  const startDate = state.mapTimeStartDate;
  const startIndex = sliderIndexForDate(windowStartDate, startDate);
  const selectedIndex = sliderIndexForDate(windowStartDate, selectedDate);
  const period = reportMapTimePeriodText();
  const sliderMarkup = reportMapRangeSliderMarkup(startIndex, selectedIndex);

  return `
    <div class="report-time-controls" aria-label="${copy("map_time_controls_aria")}">
      <div class="report-time-action">
        <button
          type="button"
          class="report-time-play"
          aria-pressed="${state.mapTimePlaybackRunning}"
          data-map-time-play-toggle
        >
          ${reportPlaybackButtonInner(state.mapTimePlaybackRunning)}
        </button>
      </div>
      <div class="report-date-slider">
        <p class="map-lab-section-note report-date-slider-summary" data-report-map-time-summary>${period}</p>
        ${sliderMarkup}
        <div class="report-date-slider-range">
          <span>${localizedReportMapDate(windowStartDate)}</span>
          <span>${localizedReportMapDate(windowEndDate)}</span>
        </div>
      </div>
    </div>
  `;
}


export function reportMapRangeSliderMarkup(startIndex, selectedIndex) {
  return `
    <div class="report-date-slider-heading">
      <span>${copy("map_start")} <strong data-map-time-start-label>${localizedReportMapDate(state.mapTimeStartDate)}</strong></span>
      <span>${copy("map_end")} <strong data-map-time-selected-label>${localizedReportMapDate(state.mapTimeSelectedDate)}</strong></span>
    </div>
    <div
      class="report-date-range-control"
      style="--range-start: ${sliderPercent(startIndex)}%; --range-end: ${sliderPercent(selectedIndex)}%;"
    >
      <input
        type="range"
        min="0"
        max="${sliderWindowDays()}"
        value="${startIndex}"
        aria-label="${copy("map_start_date_aria")}"
        data-map-date-slider="start"
      >
      <input
        class="range-end"
        type="range"
        min="0"
        max="${sliderWindowDays()}"
        value="${selectedIndex}"
        aria-label="${copy("map_selected_date_aria")}"
        data-map-date-slider="selected"
      >
    </div>
  `;
}


export function sliderPercent(index) {
  return (Math.max(0, Math.min(sliderWindowDays(), index)) / sliderWindowDays()) * 100;
}


export function localizedReportMapDate(isoDate) {
  return formatReportMapDate(isoDate, state.lang);
}


export function reportMapTimeWindow() {
  return {
    startDate: state.mapTimeStartDate,
    selectedDate: state.mapTimeSelectedDate,
  };
}


export function setReportMapSelectedDate(value) {
  const nextSelectedDate = clampIsoDate(
    value,
    state.mapTimeStartDate,
    state.mapTimeWindowEndDate
  );
  if (nextSelectedDate === state.mapTimeSelectedDate) {
    syncReportMapTimeControls();
    return Promise.resolve();
  }
  state.mapTimeSelectedDate = nextSelectedDate;
  syncReportMapTimeControls();
  return recomputeMapLabDerived();
}


export function setReportMapStartDate(value) {
  const nextStartDate = clampIsoDate(
    value,
    state.mapTimeWindowStartDate,
    state.mapTimeSelectedDate
  );
  if (nextStartDate === state.mapTimeStartDate) {
    syncReportMapTimeControls();
    return Promise.resolve();
  }
  state.mapTimeStartDate = nextStartDate;
  syncReportMapTimeControls();
  return recomputeMapLabDerived();
}


export function syncReportMapTimeControls() {
  const startIndex = sliderIndexForDate(state.mapTimeWindowStartDate, state.mapTimeStartDate);
  const selectedIndex = sliderIndexForDate(state.mapTimeWindowStartDate, state.mapTimeSelectedDate);
  const startLabel = localizedReportMapDate(state.mapTimeStartDate);
  const selectedLabel = localizedReportMapDate(state.mapTimeSelectedDate);
  document.querySelectorAll("[data-map-date-slider]").forEach((slider) => {
    slider.value = slider.dataset.mapDateSlider === "start" ? String(startIndex) : String(selectedIndex);
  });
  document.querySelectorAll(".report-date-range-control").forEach((control) => {
    control.style.setProperty("--range-start", `${sliderPercent(startIndex)}%`);
    control.style.setProperty("--range-end", `${sliderPercent(selectedIndex)}%`);
  });
  document.querySelectorAll("[data-map-time-start-label]").forEach((node) => {
    node.textContent = startLabel;
  });
  document.querySelectorAll("[data-map-time-selected-label]").forEach((node) => {
    node.textContent = selectedLabel;
  });
  document.querySelectorAll("[data-report-map-time-summary]").forEach((node) => {
    node.textContent = reportMapTimePeriodText();
  });
}


export function clampIsoDate(value, minDate, maxDate) {
  if (value < minDate) return minDate;
  if (value > maxDate) return maxDate;
  return value;
}


export function toggleReportMapPlayback() {
  if (state.mapTimePlaybackRunning) {
    stopReportMapPlayback();
    return;
  }
  startReportMapPlayback();
}


export function startReportMapPlayback() {
  if (state.mapTimePlaybackRunning) return;
  let initialUpdate = Promise.resolve();
  state.mapTimePlaybackRunning = true;
  if (state.mapTimeSelectedDate >= state.mapTimeWindowEndDate) {
    initialUpdate = setReportMapSelectedDate(state.mapTimeStartDate);
  }
  if (state.mapTimeSelectedDate >= state.mapTimeWindowEndDate) {
    state.mapTimePlaybackRunning = false;
    syncPlaybackButtons();
    return;
  }
  syncPlaybackButtons();
  void initialUpdate.finally(scheduleReportMapPlayback);
}


export function scheduleReportMapPlayback() {
  if (!state.mapTimePlaybackRunning) return;
  if (mapPlaybackTimer) window.clearTimeout(mapPlaybackTimer);
  mapPlaybackTimer = window.setTimeout(advanceReportMapPlayback, MAP_LAB_PLAYBACK_MS);
}


export function advanceReportMapPlayback() {
  mapPlaybackTimer = null;
  if (state.mapTimeSelectedDate >= state.mapTimeWindowEndDate) {
    stopReportMapPlayback();
    return;
  }
  const selectedIndex = sliderIndexForDate(
    state.mapTimeWindowStartDate,
    state.mapTimeSelectedDate
  );
  const nextDate = sliderDateForIndex(state.mapTimeWindowStartDate, selectedIndex + 1);
  const update = setReportMapSelectedDate(nextDate);
  if (nextDate >= state.mapTimeWindowEndDate) {
    void update.finally(stopReportMapPlayback);
    return;
  }
  void update.finally(scheduleReportMapPlayback);
}


export function stopReportMapPlayback() {
  if (mapPlaybackTimer) window.clearTimeout(mapPlaybackTimer);
  mapPlaybackTimer = null;
  state.mapTimePlaybackRunning = false;
  syncPlaybackButtons();
}


export function reportPlaybackButtonInner(running) {
  const icon = running
    ? `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>`
    : `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8 5.14v13.72a1 1 0 0 0 1.54.84l10.29-6.86a1 1 0 0 0 0-1.68L9.54 4.3A1 1 0 0 0 8 5.14z" /></svg>`;
  return `
    <span class="report-time-play-icon" aria-hidden="true">${icon}</span>
    <span class="report-time-play-label">${copy(running ? "map_pause" : "map_play")}</span>
  `;
}


export function syncPlaybackButtons() {
  document.querySelectorAll("[data-map-time-play-toggle]").forEach((button) => {
    button.innerHTML = reportPlaybackButtonInner(state.mapTimePlaybackRunning);
    button.setAttribute("aria-pressed", String(state.mapTimePlaybackRunning));
  });
}


export function optionCard(id, title, detail, icon) {
  const report = currentPeopleReport();
  return `
    <button class="option-card ${report.feeling === id ? "selected" : ""}" type="button" data-feeling="${id}">
      <span class="tile-icon">${icon}</span>
      <strong>${title}</strong>
      <span>${detail}</span>
    </button>
  `;
}


export function animalOptionCard(id, title, detail, icon, group, selected) {
  return `
    <button
      class="option-card ${selected ? "selected" : ""}"
      type="button"
      data-chip-group="${group}"
      data-chip-value="${escapeHtml(id)}"
    >
      <span class="tile-icon">${icon}</span>
      <strong>${escapeHtml(title)}</strong>
      <span>${escapeHtml(detail)}</span>
    </button>
  `;
}


export function subjectCard(id, title, detail, icon) {
  const isSelected = state.reportSubjects.includes(id);
  return `
    <button class="option-card ${isSelected ? "selected" : ""}" type="button" data-report-subject="${id}">
      <span class="tile-icon">${icon}</span>
      <strong>${title}</strong>
      <span>${detail}</span>
    </button>
  `;
}


export function categoryRow(id, title, detail, icon) {
  const isSelected = selectedCategories().includes(id);
  return `
    <button class="category-row ${isSelected ? "selected" : ""}" type="button" data-category="${id}">
      <span class="tile-icon">${icon}</span>
      <span><strong>${title}</strong><span>${detail}</span></span>
      <span class="checkmark">${isSelected ? "✓" : ""}</span>
    </button>
  `;
}


export function syncFrontDoorCategorySelection() {
  document.querySelectorAll(".front-door-category-list .category-row[data-category]").forEach((button) => {
    const isSelected = selectedCategories().includes(button.dataset.category);
    button.classList.toggle("selected", isSelected);
    const checkmark = button.querySelector(".checkmark");
    if (checkmark) checkmark.textContent = isSelected ? "✓" : "";
  });
  nextButton.disabled = isSubmitting || !canContinue();
}


export function chip(value, selected, group, label = value) {
  return `<button class="chip ${selected ? "selected" : ""}" type="button" data-chip-group="${group}" data-chip-value="${value}">${label}</button>`;
}


export function yesNoChips(field, value) {
  return `<div class="chip-grid">${["No", "Yes"].map((item) => chip(item, value === item, `exposure:${field}`)).join("")}</div>`;
}

export function exposureQuestion(question, field, value) {
  return `
    <div class="exposure-question">
      <p class="field-label">${question}</p>
      ${yesNoChips(field, value)}
    </div>
  `;
}


export function symptomChip(value, selected) {
  return badgeChip(value, symptomLabel(value, state.lang), selected, "symptom", SYMPTOM_BADGES);
}


export function badgeChip(value, label, selected, group, badges) {
  const badge = badges[value];
  const badgeImage = badge
    ? `<img class="symptom-badge" src="${badge}" alt="" aria-hidden="true">`
    : "";
  return `
    <button
      class="chip symptom-chip ${selected ? "selected" : ""}"
      type="button"
      data-chip-group="${group}"
      data-chip-value="${value}"
      aria-pressed="${selected}"
    >
      ${badgeImage}
      <span>${label}</span>
    </button>
  `;
}


export function textInput(field, placeholder, value) {
  return `<p class="field-label"></p><input class="text-area" data-input="${field}" placeholder="${placeholder}" value="${value}">`;
}


export function numberInput(field, value, placeholder, maxLength) {
  const count = sanitizeCountInput(value, maxLength);
  return `<div class="input-row"><input inputmode="numeric" maxlength="${maxLength}" data-input="${field}" placeholder="${placeholder}" value="${count}"></div>`;
}


export function peopleDateTarget(subject) {
  return `people:${subject}`;
}


export function dateTargetSubject(target) {
  return String(target || "").startsWith("people:") ? String(target).split(":")[1] : "";
}


export function dateTargetValue(target) {
  const subject = dateTargetSubject(target);
  if (subject) return state.peopleReports[subject]?.onset || "";
  if (target === "animals") return state.animalObservedDate;
  if (target === "environment") return state.environmentObservedDate;
  return "";
}


export function setDateTargetValue(target, value) {
  const subject = dateTargetSubject(target);
  if (subject && state.peopleReports[subject]) state.peopleReports[subject].onset = value;
  if (target === "animals") state.animalObservedDate = value;
  if (target === "environment") state.environmentObservedDate = value;
}


export function dateTargetFlowLabel(target) {
  const subject = dateTargetSubject(target);
  if (subject) return peopleReportFlowLabel(subject);
  if (target === "animals" || target === "environment") return reportFlowLabel(target);
  return "";
}


export function dateTargetModalTitle(target) {
  return dateTargetSubject(target) ? copy("calendar_title") : copy("calendar_observed_title");
}


export function dateInput(value, target) {
  const fieldValue = displayDate(value);
  const isOpen = state.datePickerTarget === target;
  return `
    <button
      class="date-field ${fieldValue ? "has-value" : ""}"
      type="button"
      data-action="open-date-picker"
      data-date-target="${target}"
      aria-haspopup="dialog"
      aria-expanded="${isOpen}"
    >
      <svg
        class="date-field-icon"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M8 2v4" />
        <path d="M16 2v4" />
        <rect width="18" height="18" x="3" y="4" rx="2" />
        <path d="M3 10h18" />
        <rect x="13.5" y="12.1" width="1.9" height="1.9" rx="0.35" fill="currentColor" stroke="none" />
        <rect x="17.4" y="12.1" width="1.9" height="1.9" rx="0.35" fill="currentColor" stroke="none" />
        <rect x="5.7" y="15.1" width="1.9" height="1.9" rx="0.35" fill="currentColor" stroke="none" />
        <rect x="9.6" y="15.1" width="1.9" height="1.9" rx="0.35" fill="currentColor" stroke="none" />
        <rect x="13.5" y="15.1" width="1.9" height="1.9" rx="0.35" fill="currentColor" stroke="none" />
        <rect x="17.4" y="15.1" width="1.9" height="1.9" rx="0.35" fill="currentColor" stroke="none" />
        <rect x="5.7" y="18.1" width="1.9" height="1.9" rx="0.35" fill="currentColor" stroke="none" />
        <rect x="9.6" y="18.1" width="1.9" height="1.9" rx="0.35" fill="currentColor" stroke="none" />
        <rect x="13.5" y="18.1" width="1.9" height="1.9" rx="0.35" fill="currentColor" stroke="none" />
      </svg>
      <span class="date-field-value">${fieldValue || copy("select_date")}</span>
      <span class="date-field-caret" aria-hidden="true"></span>
    </button>
  `;
}


export function inputValue(field) {
  if (field === "peopleZip") return currentPeopleReport().zip;
  return state[field];
}


export function zipInput(field = "peopleZip") {
  const value = inputValue(field);
  const validation = validateArizonaPostalCode(value);
  const postalError = value && !validation.ok ? postalCodeErrorMessage(validation.error) : "";
  const showStatus = state.zipDetectField === field && state.zipDetectMessage;
  const statusClass = state.zipDetectMessageKind === "error" ? "field-error" : "field-status";
  const errorId = `${field}Error`;
  const statusId = `${field}Status`;
  return `
    <div class="input-row">
      <input type="text" inputmode="numeric" autocomplete="postal-code" maxlength="5" data-input="${field}" placeholder="${t().zip}" value="${escapeHtml(value)}" aria-invalid="${Boolean(postalError)}" aria-describedby="${errorId} ${statusId}">
      <button class="detect-button" type="button" data-action="detect" ${isDetectingZip ? "disabled" : ""}>
        ${t().detect}
      </button>
    </div>
    <p class="field-error" id="${errorId}" role="alert" ${postalError ? "" : "hidden"}>${escapeHtml(postalError)}</p>
    <p class="${statusClass}" id="${statusId}" role="${state.zipDetectMessageKind === "error" ? "alert" : "status"}" ${showStatus ? "" : "hidden"}>${showStatus ? escapeHtml(state.zipDetectMessage) : ""}</p>
  `;
}


export function postalCodeErrorMessage(error) {
  if (error === POSTAL_CODE_ERROR.FORMAT) return copy("zip_format");
  if (error === POSTAL_CODE_ERROR.NOT_ARIZONA) return copy("zip_arizona");
  return copy("zip_required");
}


export function updateZipInputValidity(field, input) {
  const result = validateArizonaPostalCode(input.value);
  const message = result.ok ? "" : postalCodeErrorMessage(result.error);
  const error = screen.querySelector(`#${field}Error`);
  input.setAttribute("aria-invalid", String(!result.ok));
  if (error) {
    error.textContent = message;
    error.hidden = result.ok;
  }
  return result;
}


export function shouldUseAccountPostalCode(subject) {
  return shouldUseSessionPostalCode({
    authenticated: state.accountSignedIn,
    postalCode: state.accountPostalCode,
    subject,
  });
}


export function peopleZipPrompt(subject) {
  if (shouldUseAccountPostalCode(subject)) return "";
  return `<p class="field-label">${copy("report_zip_h")}</p>${zipInput("peopleZip")}`;
}


export function renderDatePickerModal() {
  const target = state.datePickerTarget;
  if (!target) return "";

  const selectedDate = parseOnsetDate(dateTargetValue(target));
  const selectedIsoDate = selectedDate ? isoDateFromDate(selectedDate) : "";
  const today = todayIsoDate();
  const monthDate = dateForMonthKey(state.calendarMonth);
  const monthLabel = `${MONTH_NAMES[monthDate.getMonth()]} ${monthDate.getFullYear()}`;
  const days = calendarDays(state.calendarMonth);

  return `
    <div class="calendar-modal" role="dialog" aria-modal="true" aria-labelledby="calendarTitle">
      <button class="calendar-backdrop" type="button" data-action="close-date-picker" aria-label="${copy("calendar_close")}"></button>
      <section class="calendar-sheet">
        <div class="calendar-grabber" aria-hidden="true"></div>
        <div class="calendar-sheet-heading">
          <div>
            <p class="field-label">${dateTargetFlowLabel(target)}</p>
            <h3 id="calendarTitle">${dateTargetModalTitle(target)}</h3>
          </div>
          <button class="calendar-close" type="button" data-action="close-date-picker" aria-label="${copy("calendar_close")}">x</button>
        </div>
        <div class="calendar-card">
          <div class="calendar-month-bar">
            <button class="calendar-nav" type="button" data-action="move-date-picker" data-month-delta="-1" aria-label="${copy("calendar_previous_month")}">
              <span class="calendar-chevron previous" aria-hidden="true"></span>
            </button>
            <strong>${monthLabel}</strong>
            <button class="calendar-nav" type="button" data-action="move-date-picker" data-month-delta="1" aria-label="${copy("calendar_next_month")}">
              <span class="calendar-chevron next" aria-hidden="true"></span>
            </button>
          </div>
          <div class="calendar-weekdays" aria-hidden="true">
            ${WEEK_DAYS.map((day) => `<span>${day}</span>`).join("")}
          </div>
          <div class="calendar-grid">
            ${days.map((day, index) => renderCalendarDay(day, index, selectedIsoDate, today)).join("")}
          </div>
        </div>
      </section>
    </div>
  `;
}


export function renderCalendarDay(day, index, selectedIsoDate, today) {
  if (!day) return `<span class="calendar-day-placeholder" aria-hidden="true" data-calendar-blank="${index}"></span>`;
  const date = parseIsoDate(day);
  const isSelected = day === selectedIsoDate;
  const isToday = day === today;
  const isFuture = isFutureIsoDate(day, today);
  return `
    <button
      class="calendar-day ${isSelected ? "selected" : ""} ${isToday ? "today" : ""} ${isFuture ? "future" : ""}"
      type="button"
      data-action="select-date"
      data-date="${day}"
      aria-pressed="${isSelected}"
      ${isFuture ? 'aria-disabled="true" disabled' : ""}
      ${isToday ? 'aria-current="date"' : ""}
    >
      ${date.getDate()}
    </button>
  `;
}


export function activeZipField() {
  const category = reportCategoryForStep();
  if (category === "people") return "peopleZip";
  if (category === "animals" || category === "environment") return observationFieldNames(category).zip;
  return "";
}


export function setZipField(field, value) {
  if (field === "peopleZip") {
    applyManualHumanZip(currentPeopleReport(), value);
    if (peopleSubjectForStep() === "self") state.selfReportUsesAccountPostalCode = false;
  }
  if (field === "animalZip" || field === "environmentZip") {
    const previousValue = state[field];
    state[field] = value;
    if (previousValue !== value) clearReportLocationForZipChange();
  }
}


export function setPeopleZipFromDetectedLocation(field, zipCode, locationH3) {
  if (field !== "peopleZip") {
    setZipField(field, zipCode);
    return;
  }
  applyDetectedHumanLocation(currentPeopleReport(), zipCode, locationH3);
}


export function setZipDetectMessage(field, message, kind = "info") {
  state.zipDetectField = field;
  state.zipDetectMessage = message;
  state.zipDetectMessageKind = kind;
}


export function clearZipDetectMessage(field) {
  if (state.zipDetectField !== field) return;
  state.zipDetectField = "";
  state.zipDetectMessage = "";
  state.zipDetectMessageKind = "";
  const status = screen.querySelector(`#${field}Status`);
  if (status) {
    status.textContent = "";
    status.hidden = true;
  }
}


export function invalidateZipDetectionAttempt() {
  zipDetectionGeneration += 1;
  if (!isDetectingZip) return;
  isDetectingZip = false;
  const field = state.zipDetectField;
  if (field) clearZipDetectMessage(field);
  screen.querySelector('[data-action="detect"]')?.removeAttribute("disabled");
}


export function beginZipDetectionAttempt(field) {
  const attempt = {
    generation: ++zipDetectionGeneration,
    field,
    step: currentStep(),
    reportIdentity: field === "peopleZip" ? currentPeopleReport() : state.categories,
  };
  isDetectingZip = true;
  return attempt;
}


export function zipDetectionAttemptContextIsCurrent(attempt) {
  const reportIdentity = attempt.field === "peopleZip" ? currentPeopleReport() : state.categories;
  return (
    currentStep() === attempt.step &&
    activeZipField() === attempt.field &&
    reportIdentity === attempt.reportIdentity
  );
}


export function zipDetectionAttemptIsCurrent(attempt) {
  return (
    isDetectingZip &&
    attempt.generation === zipDetectionGeneration &&
    zipDetectionAttemptContextIsCurrent(attempt)
  );
}


export function normalizeCurrentStepPostalCode() {
  const field = activeZipField();
  if (!field) return;
  const value = inputValue(field);
  const validation = validateArizonaPostalCode(value);
  if (validation.ok && value !== validation.value) {
    setZipField(field, validation.value);
  }
}


export function openDatePicker(target) {
  const selectedDate = parseOnsetDate(dateTargetValue(target)) || new Date();
  state.datePickerTarget = target;
  state.calendarMonth = monthKeyForDate(selectedDate);
  render();
}


export function closeDatePicker() {
  state.datePickerTarget = "";
}


export function moveDatePickerMonth(delta) {
  const currentMonth = dateForMonthKey(state.calendarMonth);
  state.calendarMonth = monthKeyForDate(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + delta, 1));
}


export function selectDatePickerDate(value) {
  const selectedDate = parseIsoDate(value);
  if (!selectedDate || !state.datePickerTarget) return;
  const selectedIsoDate = isoDateFromDate(selectedDate);
  if (isFutureIsoDate(selectedIsoDate)) return;
  setDateTargetValue(state.datePickerTarget, selectedIsoDate);
  closeDatePicker();
  render();
}


export async function detectZipForCurrentStep() {
  const field = activeZipField();
  if (!field || isDetectingZip) return;
  if (!("geolocation" in navigator)) {
    setZipDetectMessage(field, copy("zip_detect_no_geo"), "error");
    render();
    screen.querySelector(`[data-input="${field}"]`)?.focus();
    return;
  }

  const attempt = beginZipDetectionAttempt(field);
  const locationInteractionAtStart = reportLocationInteractionGeneration;
  const peopleReportAtStart = field === "peopleZip" ? currentPeopleReport() : null;
  const peopleZipAtStart = peopleReportAtStart?.zip;
  setZipDetectMessage(field, copy("zip_detecting"));
  render();

  try {
    const position = await currentBrowserPosition();
    if (field === "peopleZip") {
      const locationH3 = locationH3FromCoordinates(position.coords);
      const zipCode = await reverseGeocodeHumanZip(position.coords);
      if (
        !zipDetectionAttemptIsCurrent(attempt) ||
        currentPeopleReport() !== peopleReportAtStart ||
        !canApplyDetectedHumanLocation(peopleReportAtStart, peopleZipAtStart)
      ) {
        return;
      }
      applyDetectedHumanLocation(peopleReportAtStart, zipCode, locationH3);
    } else {
      const zipCode = await reverseGeocodeZip(position.coords);
      if (
        !zipDetectionAttemptIsCurrent(attempt) ||
        locationInteractionAtStart !== reportLocationInteractionGeneration
      ) {
        return;
      }
      setZipField(field, zipCode);
      state.reportLocation = setReportLocationBrowserCoordinates(state.reportLocation, position.coords);
    }
    setZipDetectMessage(field, copy("zip_detected"));
  } catch (error) {
    if (!zipDetectionAttemptIsCurrent(attempt)) return;
    setZipDetectMessage(field, locationDetectionMessage(error), "error");
  } finally {
    if (attempt.generation === zipDetectionGeneration) {
      const contextIsCurrent = zipDetectionAttemptContextIsCurrent(attempt);
      isDetectingZip = false;
      if (contextIsCurrent) {
        render();
        if (state.zipDetectField === field && state.zipDetectMessageKind === "error") {
          screen.querySelector(`[data-input="${field}"]`)?.focus();
        }
      } else {
        zipDetectionGeneration += 1;
        if (state.zipDetectField === field) clearZipDetectMessage(field);
      }
    }
  }
}


export async function reverseGeocodeHumanZip(coords) {
  const zipGeometry = await loadArizonaZipGeometry();
  const match = (zipGeometry.features || []).find((feature) => featureContainsCoordinate(feature, coords));
  const zipCode = String(match?.properties?.GEOID || match?.properties?.ZCTA5 || "");
  if (!zipCode) throw new Error("no_zip");
  const validation = validateArizonaPostalCode(zipCode);
  if (!validation.ok) throw new Error("not_arizona");
  return validation.value;
}


export function currentBrowserPosition() {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      maximumAge: 60000,
      timeout: 8000,
    });
  });
}


export async function reverseGeocodeZip(coords) {
  const params = new URLSearchParams({
    longitude: String(coords.longitude),
    latitude: String(coords.latitude),
    types: "postcode",
    country: "us",
    limit: "1",
    access_token: mapboxToken(),
  });
  const response = await fetch(`${MAPBOX_REVERSE_GEOCODE_URL}?${params.toString()}`);
  if (!response.ok) throw new Error("mapbox");
  const body = await response.json();
  const feature = body.features?.[0];
  const zipCode = extractZipCode(feature);
  if (!zipCode) throw new Error("no_zip");
  if (!isArizonaMapboxFeature(feature)) throw new Error("not_arizona");
  const validation = validateArizonaPostalCode(zipCode);
  if (!validation.ok) throw new Error("not_arizona");
  return validation.value;
}


export function extractZipCode(feature) {
  const values = [
    feature?.properties?.name,
    feature?.properties?.postcode,
    feature?.properties?.full_address,
    feature?.properties?.place_formatted,
    feature?.properties?.context?.postcode?.name,
    feature?.properties?.context?.postcode?.postcode,
    feature?.text,
    feature?.place_name,
  ];
  const match = values.map((value) => String(value || "").match(/\b\d{5}(?:-\d{4})?\b/)).find(Boolean);
  return match ? match[0].slice(0, 5) : "";
}


export function isArizonaMapboxFeature(feature) {
  const context = feature?.properties?.context || {};
  const regionValues = [
    context.region?.region_code,
    context.region?.region_code_full,
    context.region?.name,
    feature?.properties?.region,
    feature?.properties?.place_formatted,
    feature?.place_name,
  ].map((value) => String(value || "").toLowerCase());
  return regionValues.some((value) => value === "az" || value === "us-az" || value.includes("arizona"));
}


export function locationDetectionMessage(error) {
  if (error?.code === 1) return copy("zip_detect_denied");
  if (error?.code === 2) return copy("zip_detect_unavailable");
  if (error?.code === 3) return copy("zip_detect_timeout");
  if (error?.message === "no_zip") return copy("zip_detect_no_zip");
  if (error?.message === "not_arizona") return copy("zip_detect_not_arizona");
  return copy("zip_detect_error");
}


export function exposuresComplete(exposures) {
  return (
    yesNoAnswered(exposures.massGathering) &&
    (exposures.massGathering !== "Yes" || exposures.massGatheringEvent.trim().length > 0) &&
    yesNoAnswered(exposures.liveAnimalContact) &&
    (
      exposures.liveAnimalContact !== "Yes" ||
      (yesNoAnswered(exposures.liveAnimalBitten) && yesNoAnswered(exposures.liveAnimalAppearedSick))
    ) &&
    yesNoAnswered(exposures.deadAnimalContact) &&
    yesNoAnswered(exposures.sickIndividualContact) &&
    yesNoAnswered(exposures.tickOrInsectBite)
  );
}

export function travelComplete(exposures) {
  return (
    yesNoAnswered(exposures.recentTravel) &&
    (exposures.recentTravel !== "Yes" || recentTravelLocationComplete(exposures, state.travelAutocomplete))
  );
}

export function recentTravelLocationComplete(exposures, autocomplete = emptyLocationAutocompleteState()) {
  return recentTravelLocationIsComplete(exposures, autocomplete);
}

export function yesNoAnswered(value) {
  return value === "Yes" || value === "No";
}

export function setExposureAnswer(report, field, value) {
  report.exposures[field] = value;
  if (field === "massGathering" && value !== "Yes") {
    report.exposures.massGatheringEvent = "";
  }
  if (field === "liveAnimalContact" && value !== "Yes") {
    report.exposures.liveAnimalBitten = "";
    report.exposures.liveAnimalAppearedSick = "";
  }
  if (field === "recentTravel" && value !== "Yes") {
    report.exposures.recentTravelLocation = "";
    report.exposures.recentTravelLocationSelection = null;
    report.exposures.recentTravelLocationFallbackAllowed = false;
    resetTravelLocationAutocomplete();
  }
}

export function canContinue() {
  const report = currentPeopleReport();
  if (currentStep() === "frontDoor") return selectedCategories().length > 0;
  if (currentStep() === "peopleSubject") return selectedPeopleSubjects().length > 0;
  if (currentStep() === "peopleSignIn") return false;
  if (isPeopleFeelingStep()) return report.feeling;
  if (isPeopleGoodCheckInStep()) return validateArizonaPostalCode(report.zip).ok;
  if (isPeopleSymptomsStep()) {
    return (
      report.symptoms.length > 0 &&
      validateArizonaPostalCode(report.zip).ok &&
      isPositiveIntegerText(report.sickCount)
    );
  }
  if (isPeopleAssessmentStep()) {
    return report.onset && report.diagnosed && (report.diagnosed !== "Yes" || report.diagnosis) && report.calledInSick;
  }
  if (isPeopleExposuresStep()) return exposuresComplete(report.exposures);
  if (isPeopleTravelStep()) return travelComplete(report.exposures);
  if (currentStep() === "animalCategory") return Boolean(state.animalGroup);
  if (currentStep() === "animalSpecies") return Boolean(state.animalSpecies && state.animalType);
  if (currentStep() === "animalCondition") return Boolean(state.animalStatus);
  if (currentStep() === "animalCount") return isPositiveIntegerText(state[activeAnimalCountField(state.animalStatus)]);
  if (currentStep() === "animalDate") return isAnimalFoundDateInRange(state.animalObservedDate);
  if (currentStep() === "animalLocation") return validateArizonaPostalCode(state.animalZip).ok;
  if (currentStep() === "animalReporterFeeling") return Boolean(state.animalReporterFeeling);
  if (currentStep() === "environmentIssue") return Boolean(state.environmentIssueType);
  if (currentStep() === "environmentConcern") {
    if (state.environmentIssueType === "insect_vectors") return Boolean(state.insectVectorType);
    if (state.environmentIssueType === "environmental_concern") return state.environmentConcerns.length > 0;
    return false;
  }
  if (currentStep() === "environmentLocation") return validateArizonaPostalCode(state.environmentZip).ok;
  if (currentStep() === "environmentDate") return isEnvironmentObservedDateInRange(state.environmentObservedDate);
  if (currentStep() === "environmentReporterFeeling") return Boolean(state.environmentReporterFeeling);
  if (currentStep() === "reportLocation") return reportLocationDecisionResolved(state.reportLocation);
  return false;
}


export function isLastBeforeDone() {
  return steps()[state.step + 1] === "done";
}


export function isSubmitStep() {
  const nextStep = steps()[state.step + 1];
  return isLastBeforeDone() || nextStep === POST_SUBMIT_REGISTRATION_STEP;
}


export function routeForStep(stepName) {
  return ROUTES[stepName] || ROUTES.frontDoor;
}


export function stepForRoute(route) {
  return STEPS_BY_ROUTE[normalizeRoute(route)] || "";
}


export function normalizeRoute(route) {
  const [path] = String(route || "/").split(/[?#]/);
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return normalized.length > 1 ? normalized.replace(/\/+$/, "") : "/";
}


export function navigateToStep(stepName, options = {}) {
  const targetStep = ROUTES[stepName] ? stepName : "frontDoor";
  if (router) {
    router.navigate(routeForStep(targetStep), options);
    return;
  }
  activateRoute(targetStep);
}


export function activateRoute(stepName) {
  const accountResolvedStep = routeStepForAccountSession(stepName, state.accountSignedIn);
  if (accountResolvedStep !== stepName) {
    if (router) {
      router.navigate(routeForStep("done"), { historyAPIMethod: "replaceState" });
      return;
    }
    stepName = accountResolvedStep;
  }
  closeDatePicker();
  const gatedStepName = stepName.startsWith("peopleOther") && !state.accountSignedIn
    ? "peopleSignIn"
    : stepName;
  if (gatedStepName !== currentStep()) invalidateZipDetectionAttempt();
  if (gatedStepName === "frontDoor") {
    clearAccountReportSubmissionBatch(window.sessionStorage);
  }
  prepareStateForRoute(gatedStepName);
  const stepIndex = steps().indexOf(gatedStepName);
  state.step = stepIndex === -1 ? 0 : stepIndex;
  render();
}


export function prepareStateForRoute(stepName) {
  if (stepName.startsWith("people")) preparePeopleStateForRoute(stepName);
  if (stepName.startsWith("animal")) ensureSelected(state.categories, "animals");
  if (stepName.startsWith("environment")) ensureSelected(state.categories, "environment");
}


export function preparePeopleStateForRoute(stepName) {
  const isReporterHealthStep = reporterAddsPeople() && stepName.startsWith("peopleSelf");
  if (!isReporterHealthStep) ensureSelected(state.categories, "people");

  if (stepName.includes("Self")) {
    ensureSelected(state.reportSubjects, "self");
  }
  if (stepName.includes("Other") || stepName === "peopleSignIn") {
    ensureSelected(state.reportSubjects, "other");
  }
  if (stepName === "peopleSignIn") {
    state.fakeSignedIn = false;
  }
  if (stepName.includes("peopleOther") && stepName !== "peopleSignIn") {
    state.fakeSignedIn = true;
  }
  if (stepName.endsWith("Symptoms") || stepName.endsWith("Assessment")) {
    state.peopleReports[peopleSubjectForStep(stepName)].feeling = "sick";
  }
  if (stepName.endsWith("Exposures") || stepName.endsWith("Travel")) {
    state.peopleReports[peopleSubjectForStep(stepName)].feeling = "sick";
  }
  if (stepName.endsWith("GoodCheckIn")) {
    state.peopleReports[peopleSubjectForStep(stepName)].feeling = "good";
  }
}


export function ensureSelected(list, value) {
  if (!list.includes(value)) list.push(value);
}


export function initializeRouter() {
  const weeklyCheckinCategory = weeklyCheckinCategoryFromSearch(window.location.search);
  if (weeklyCheckinCategory) state.categories = [weeklyCheckinCategory];
  router = new Navigo("/", { hash: true, noMatchWarning: true });
  Object.values(ROUTES).forEach((route) => {
    router.on(route, () => activateRoute(stepForRoute(route)));
  });
  router.notFound(() => navigateToStep("frontDoor", { historyAPIMethod: "replaceState" }));

  if (!window.location.hash) {
    navigateToStep("frontDoor", { historyAPIMethod: "replaceState" });
    return;
  }

  const resolved = router.resolve();
  if (!resolved) navigateToStep("frontDoor", { historyAPIMethod: "replaceState" });
}


export function trackScreenView(screenName) {
  if (lastTrackedScreen === screenName) return;
  if (shouldDeferAccountResumeTracking(screenName)) return;
  const domain = reportDomainForScreen(screenName);
  lastTrackedScreen = screenName;
  analytics.trackPageView(screenName);
  if (domain) trackReportFlowStarted(domain, screenName);
  analytics.trackEvent(ENGAGEMENT_EVENTS.REPORT_STEP_VIEWED, {
    ...(domain ? { domain } : {}),
    step: screenName,
  });
}


export function trackReportFlowStarted(domain, step) {
  if (reportFlowStarted) return;
  reportFlowStarted = true;
  analytics.trackEvent(ENGAGEMENT_EVENTS.REPORT_STARTED, { domain, step });
}


export function trackReportDomainSelected(domain) {
  analytics.trackEvent(ENGAGEMENT_EVENTS.REPORT_DOMAIN_SELECTED, { domain });
}


export function trackReportValidationBlocked(validationReason) {
  const domain = reportDomainForScreen(lastTrackedScreen);
  analytics.trackEvent(ENGAGEMENT_EVENTS.REPORT_VALIDATION_BLOCKED, {
    ...(domain ? { domain } : {}),
    step: lastTrackedScreen,
    validation_reason: validationReason,
  });
}


export function shouldDeferAccountResumeTracking(screenName) {
  if (accountSessionResolvedForAnalytics || screenName !== "people-sign-in") return false;
  const requestedRoute = normalizeRoute(window.location.hash.replace(/^#/, "") || "/");
  return requestedRoute.startsWith("/people/other/");
}


export function buildCommunityReportPayloads(submittedAt) {
  const categories = selectedCategories();
  const exactLocationCategories = categories.filter((category) => category === "animals" || category === "environment");
  const confirmedCoordinates = confirmedReportCoordinates(state.reportLocation);
  const confirmedLocationH3 = locationH3FromCoordinates(confirmedCoordinates);

  if (animalReporterAddsPeople()) {
    return [
      buildCommunityReportPayload(
        {
          ...payloadStateForSubject("self"),
          zip: state.peopleReports.self.zip || selectedExactLocationZip(),
        },
        ["people"],
        submittedAt,
        { reportLocationH3: confirmedLocationH3 }
      ),
      buildCommunityReportPayload(state, exactLocationCategories, submittedAt, { reportCoordinates: confirmedCoordinates }),
    ];
  }
  if (categories.includes("people") && exactLocationCategories.length > 0) {
    return [
      ...selectedPeopleSubjects().map((subject) => (
        buildCommunityReportPayload(
          payloadStateForSubject(subject),
          ["people"],
          submittedAt,
          { reportLocationH3: confirmedLocationH3 }
        )
      )),
      buildCommunityReportPayload(state, exactLocationCategories, submittedAt, { reportCoordinates: confirmedCoordinates }),
    ];
  }
  if (environmentReporterAddsPeople()) {
    return [
      buildCommunityReportPayload(
        {
          ...payloadStateForSubject("self"),
          zip: state.peopleReports.self.zip || selectedExactLocationZip(),
        },
        ["people"],
        submittedAt,
        { reportLocationH3: confirmedLocationH3 }
      ),
      buildCommunityReportPayload(state, exactLocationCategories, submittedAt, { reportCoordinates: confirmedCoordinates }),
    ];
  }
  if (!categories.includes("people")) {
    return [buildCommunityReportPayload(state, categories, submittedAt, { reportCoordinates: confirmedCoordinates })];
  }

  return selectedPeopleSubjects().map((subject, index) => {
    const reportCategories = index === 0 ? categories : ["people"];
    return buildCommunityReportPayload(payloadStateForSubject(subject), reportCategories, submittedAt);
  });
}


export function buildCommunityReportPayload(
  payloadState,
  categories,
  submittedAt,
  { reportCoordinates = null, reportLocationH3 = null } = {}
) {
  const resolvedLocationH3 = categories.includes("people") &&
    !categories.some((category) => category === "animals" || category === "environment")
    ? reportLocationH3 || payloadState.locationH3
    : null;
  return buildPayloadFromState({
    state: payloadState,
    categories,
    submittedAt,
    reportCoordinates,
    reportLocationH3: resolvedLocationH3,
    idFactory: () => crypto.randomUUID(),
  });
}


export function payloadStateForSubject(subject) {
  const report = state.peopleReports[subject];
  return {
    ...state,
    feeling: report.feeling,
    symptoms: report.symptoms,
    otherSymptom: report.otherSymptom,
    sickCount: report.sickCount,
    zip: report.zip,
    locationH3: report.locationH3,
    onset: report.onset,
    diagnosed: report.diagnosed,
    diagnosis: report.diagnosis,
    calledInSick: report.calledInSick,
    exposures: report.exposures,
  };
}


export function authenticatedAccountSubmissionIsComplete(payloads, result) {
  return authenticatedSubmissionIsComplete({
    accountSignedIn: state.accountSignedIn,
    reportSubjects: selectedPeopleSubjects(),
    payloadCount: payloads.length,
    result,
  });
}


export function isCurrentAuthenticatedAnotherPersonSubmission() {
  return isAuthenticatedAnotherPersonSubmission({
    accountSignedIn: state.accountSignedIn,
    reportSubjects: selectedPeopleSubjects(),
  });
}


export function validateBuiltPayloads(payloads) {
  const invalidGoodPeopleReport = payloads.find((payload) => (
    payload.category.some((category) => category === "animals" || category === "environment") &&
    payload.category.includes("people") &&
    payload.feeling === "good" &&
    (!Array.isArray(payload.observations) || payload.observations.length === 0)
  ));
  return invalidGoodPeopleReport ? copy("people_good_block") : "";
}


export function validateReportLocationDecision() {
  if (!shouldIncludeReportLocationStep()) return "";
  return reportLocationDecisionResolved(state.reportLocation) ? "" : copy("report_location_unresolved");
}


export function submissionFailureMessage(outcome) {
  if (outcome.kind === "partial_rejection") return copy("submission_partial_rejection");
  if (outcome.kind === "rejected") return copy("submission_rejected");
  return copy("submission_malformed");
}


export function storeCompletedReports(payloads) {
  try {
    const reports = JSON.parse(localStorage.getItem("community-reports") || "[]");
    localStorage.setItem("community-reports", JSON.stringify([...payloads, ...reports].slice(0, 20)));
    updateReportMapSources();
  } catch {
    // Local report history is optional after the backend has confirmed completion.
  }
}


export function clearCompletedReportsFromPendingQueue(payloads) {
  try {
    removePendingReports(localStorage, payloads);
  } catch {
    // A stale local queue must not turn a confirmed backend result into a failure.
  }
}


export function saveSubmissionToPendingQueue(payloads, attachmentSelections) {
  if (Object.keys(attachmentSelections).length > 0) return false;
  try {
    queuePendingReports(localStorage, payloads);
    return true;
  } catch {
    return false;
  }
}

export function clearOrdinarySubmissionAttempt() {
  ordinarySubmissionAttempt = null;
}


export function payloadsForOrdinarySubmission(submittedAt) {
  const candidatePayloads = buildCommunityReportPayloads(submittedAt);
  const fingerprint = JSON.stringify(candidatePayloads.map((payload) => {
    const stablePayload = { ...payload };
    delete stablePayload.id;
    delete stablePayload.submitted_at;
    return stablePayload;
  }));
  if (ordinarySubmissionAttempt?.fingerprint === fingerprint) {
    return ordinarySubmissionAttempt.payloads;
  }
  ordinarySubmissionAttempt = { fingerprint, payloads: candidatePayloads };
  return candidatePayloads;
}


export async function submitReport() {
  if (isSubmitting) return;

  isSubmitting = true;
  nextButton.disabled = true;
  try {
    const selectionError = validateSubmissionPhotos();
    if (selectionError) {
      setSubmissionStatus(selectionError);
      trackReportValidationBlocked("invalid");
      return;
    }

    const reducedPhotoCount = await processOversizedPhotos();
    const finalPhotoError = validateSubmissionPhotos(true);
    if (finalPhotoError) {
      setSubmissionStatus(finalPhotoError);
      trackReportValidationBlocked("invalid");
      return;
    }
    const locationDecisionError = validateReportLocationDecision();
    if (locationDecisionError) {
      setSubmissionStatus(locationDecisionError);
      trackReportValidationBlocked("required");
      navigateToStep("reportLocation");
      return;
    }

    const submittedAt = new Date().toISOString();
    const authenticatedAnotherPersonSubmission = isCurrentAuthenticatedAnotherPersonSubmission();
    let payloads = authenticatedAnotherPersonSubmission
      ? buildCommunityReportPayloads(submittedAt)
      : payloadsForOrdinarySubmission(submittedAt);
    if (authenticatedAnotherPersonSubmission) {
      clearOrdinarySubmissionAttempt();
      const batchId = ensureAccountReportSubmissionBatch();
      payloads = assignAccountReportSubmissionIds(
        window.sessionStorage,
        batchId,
        selectedPeopleSubjects(),
        payloads,
        () => crypto.randomUUID()
      );
    }
    const payloadError = validateBuiltPayloads(payloads);
    if (payloadError) {
      submitStatus.textContent = payloadError;
      trackReportValidationBlocked("invalid");
      return;
    }
    const postSubmitStep = postSubmitStepForPayloads(payloads, state.accountSignedIn);
    const attachmentSelections = attachmentSelectionsByRecord(payloads);
    const reductionStatus = reducedPhotoCount > 0
      ? `Reduced ${reducedPhotoCount} oversized photo${reducedPhotoCount === 1 ? "" : "s"} client-side. `
      : "";

    payloadPreview.textContent = JSON.stringify(previewPayload(payloads, attachmentSelections), null, 2);
    setSubmissionStatus(
      "Checking the report service...",
      `${reductionStatus}Checking backend contract at ${apiUrl("/openrpc.json")}...`
    );

    let response;
    try {
      await verifyBackendContract();
      setSubmissionStatus(
        "Uploading report photos...",
        `${reductionStatus}Creating attachment uploads at ${apiUrl("/community-reports/attachments/uploads")}...`
      );
      const recordsWithAttachments = await uploadAttachmentsForRecords(payloads);
      const params = { records: recordsWithAttachments };
      setSubmissionStatus(
        "Submitting your report...",
        `${reductionStatus}Submitting report to ${apiUrl("/community-reports")}...`
      );
      response = await fetch(apiUrl("/community-reports"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(params),
      });
    } catch (error) {
      const queued = saveSubmissionToPendingQueue(payloads, attachmentSelections);
      setSubmissionStatus(
        copy(queued ? "submission_queued" : "submission_queue_failed"),
        queued
          ? `${reductionStatus}Queued locally after backend submit failed while using ${apiOrigin()}: ${error.message}`
          : `${reductionStatus}Backend submit failed and the report could not be queued locally: ${error.message}`
      );
      analytics.trackEvent(
        queued ? ENGAGEMENT_EVENTS.REPORT_QUEUED_OFFLINE : ENGAGEMENT_EVENTS.REPORT_SUBMIT_FAILED,
        {
        status: queued ? "queued_offline" : "local_queue_failed",
        online: window.navigator?.onLine === true,
        }
      );
      return;
    }

    if (!response.ok) {
      const queued = response.status >= 500 &&
        saveSubmissionToPendingQueue(payloads, attachmentSelections);
      setSubmissionStatus(
        copy(queued
          ? "submission_queued"
          : response.status >= 500
            ? "submission_queue_failed"
            : "submission_server_failure"),
        queued
          ? `${reductionStatus}Queued locally after report endpoint returned HTTP ${response.status}.`
          : `${reductionStatus}Report endpoint returned HTTP ${response.status}.`
      );
      analytics.trackEvent(
        queued ? ENGAGEMENT_EVENTS.REPORT_QUEUED_OFFLINE : ENGAGEMENT_EVENTS.REPORT_SUBMIT_FAILED,
        {
        status: queued ? "queued_offline" : "server_error",
        online: window.navigator?.onLine === true,
        }
      );
      return;
    }

    let result;
    try {
      result = await response.json();
    } catch {
      result = null;
    }
    const outcome = classifySubmissionResult(result, payloads.length);
    const authenticatedSubmissionComplete = authenticatedAccountSubmissionIsComplete(payloads, result || {});
    if (!outcome.complete) {
      setSubmissionStatus(
        submissionFailureMessage(outcome),
        `${reductionStatus}Backend submission outcome was ${outcome.kind} (${outcome.accepted} accepted, ${outcome.deduped} deduped, ${outcome.rejected} rejected).`
      );
      analytics.trackEvent(ENGAGEMENT_EVENTS.REPORT_SUBMIT_FAILED, {
        status: outcome.kind,
        online: window.navigator?.onLine === true,
      });
      return;
    }

    clearCompletedReportsFromPendingQueue(payloads);
    clearOrdinarySubmissionAttempt();
    storeCompletedReports(payloads);
    try {
      state.communityPulseReceipts = communityPulse.prependReceipts(
        localStorage,
        state.communityPulseReceipts,
        payloads
      );
    } catch {
      // A local receipt is optional after the backend has confirmed completion.
    }
    await Promise.all([
      loadCommunityReportCount(),
      loadRecentCommunityReports({ deferIfBusy: true }),
    ]);
    const attachmentsReceived = Number.isInteger(result.attachments_received)
      ? `, ${result.attachments_received} attachments received`
      : "";
    setSubmissionStatus(
      copy("submission_success"),
      `${reductionStatus}Stored as incident projection (${result.accepted} accepted, ${result.deduped} deduped${attachmentsReceived}).`
    );
    analytics.trackEvent(ENGAGEMENT_EVENTS.REPORT_SUBMITTED, {
      status: outcome.status,
      online: true,
    });
    analytics.trackEvent(ENGAGEMENT_EVENTS.REPORT_COMPLETED, {
      status: outcome.status,
      online: true,
    });
    if (authenticatedSubmissionComplete) {
      clearAccountReportDraft(localStorage);
      clearAccountReportSubmissionBatch(window.sessionStorage);
    }
    navigateToStep(postSubmitStep);
  } catch (error) {
    setSubmissionStatus(`We could not finish preparing your report: ${error.message}`, error.message);
    analytics.trackEvent(ENGAGEMENT_EVENTS.REPORT_SUBMIT_FAILED, {
      status: "preparation_failed",
      online: window.navigator?.onLine === true,
    });
  } finally {
    isSubmitting = false;
    render();
  }
}


async function loadRecentCommunityReports({ deferIfBusy = false } = {}) {
  if (isLoadingCommunityPulse) {
    if (deferIfBusy) communityPulseDeferredRefresh = true;
    return;
  }
  const requestScope = state.communityPulseScope;
  const county = requestScope === "county" ? state.communityPulseCounty : "";
  if (requestScope === "county" && !county) {
    state.communityPulseStatus = "ready";
    renderCommunityPulse();
    return;
  }

  isLoadingCommunityPulse = true;
  if (!hasLoadedCommunityPulseSuccessfully && communityPulseItems().length === 0) {
    const previousStatus = communityPulseStatusText(0);
    state.communityPulseStatus = "loading";
    if (communityPulseStatusText(0) !== previousStatus) renderCommunityPulseStatus();
  }
  const previousItems = communityPulseItems();
  const previousFeedFingerprint = communityPulse.visibleFeedFingerprint(previousItems);
  const previousStatus = communityPulseStatusText(previousItems.length);
  const previousCountyAvailable = Boolean(state.communityPulseCounty);
  let requestStillCurrent = true;
  try {
    const recentReportsUrl = new URL(apiUrl("/community-reports/recent"));
    recentReportsUrl.searchParams.set("limit", String(COMMUNITY_PULSE_LIMIT));
    if (county) recentReportsUrl.searchParams.set("county", county);
    const response = await fetch(recentReportsUrl);
    if (!response.ok) throw new Error(`Recent reports endpoint returned HTTP ${response.status}`);
    const result = await response.json();
    if (result.error) throw new Error(result.error);
    if (!Array.isArray(result.items)) throw new Error("Recent reports response did not include items");
    const currentCounty = state.communityPulseScope === "county" ? state.communityPulseCounty : "";
    requestStillCurrent = state.communityPulseScope === requestScope && currentCounty === county;
    if (!requestStillCurrent) return;

    const reconciled = communityPulse.reconcileReceipts(
      state.communityPulseReceipts,
      result.items
    );
    state.communityPulseReceipts = communityPulse.saveReceipts(localStorage, reconciled.receipts);
    state.communityPulseBackendItems = reconciled.backendItems;
    state.communityPulseCounty = communityPulseCountyFromReceipts();
    state.communityPulseStatus = "ready";
    hasLoadedCommunityPulseSuccessfully = true;
  } catch {
    const currentCounty = state.communityPulseScope === "county" ? state.communityPulseCounty : "";
    requestStillCurrent = state.communityPulseScope === requestScope && currentCounty === county;
    if (requestStillCurrent) {
      state.communityPulseStatus = communityPulseItems().length > 0 ? "stale" : "error";
    }
  } finally {
    isLoadingCommunityPulse = false;
    if (requestStillCurrent) {
      const currentItems = communityPulseItems();
      const feedChanged = (
        communityPulse.visibleFeedFingerprint(currentItems) !== previousFeedFingerprint
      );
      if (feedChanged || Boolean(state.communityPulseCounty) !== previousCountyAvailable) {
        renderCommunityPulse();
      } else if (communityPulseStatusText(currentItems.length) !== previousStatus) {
        renderCommunityPulseStatus();
      }
    }
    if (communityPulseDeferredRefresh) {
      communityPulseDeferredRefresh = false;
      loadRecentCommunityReports({ deferIfBusy: true });
    }
  }
}

function communityPulseCountyFromReceipts() {
  const receipt = state.communityPulseReceipts.find(
    (item) => item.summary?.location?.level === "county"
  );
  return receipt?.summary?.location?.label || "";
}

function communityPulseItems() {
  const county = state.communityPulseScope === "county" ? state.communityPulseCounty : null;
  return communityPulse.feedItems(
    state.communityPulseReceipts,
    state.communityPulseBackendItems,
    county
  );
}

function renderCommunityPulse() {
  const slots = document.querySelectorAll(".community-pulse-slot");
  if (slots.length === 0) return;
  const items = communityPulseItems();
  slots.forEach((slot) => {
    let section = slot.querySelector(".community-pulse");
    if (!section) {
      slot.innerHTML = communityPulseShellMarkup();
      section = slot.querySelector(".community-pulse");
    }
    updateCommunityPulseSection(section, items);
  });
}

function renderCommunityPulseStatus() {
  const items = communityPulseItems();
  const status = communityPulseStatusText(items.length);
  document.querySelectorAll(".community-pulse-status").forEach((element) => {
    element.textContent = status;
  });
}

function updateCommunityPulseSection(section, items) {
  const countyAvailable = Boolean(state.communityPulseCounty);
  const status = communityPulseStatusText(items.length);
  section.querySelector('[data-pulse-copy="eyebrow"]').textContent = copy("pulse_recent_reports");
  section.querySelector('[data-pulse-copy="title"]').textContent = copy("pulse_title");
  section.querySelector(".community-pulse-toggle").setAttribute("aria-label", copy("pulse_area_aria"));
  const allButton = section.querySelector('[data-pulse-scope="all"]');
  const countyButton = section.querySelector('[data-pulse-scope="county"]');
  allButton.textContent = copy("pulse_all_recent");
  countyButton.textContent = copy("pulse_in_your_area");
  allButton.classList.toggle("selected", state.communityPulseScope === "all");
  allButton.setAttribute("aria-pressed", String(state.communityPulseScope === "all"));
  countyButton.classList.toggle("selected", state.communityPulseScope === "county");
  countyButton.setAttribute("aria-pressed", String(state.communityPulseScope === "county"));
  countyButton.disabled = !countyAvailable;
  section.querySelector(".community-pulse-status").textContent = status;

  const list = section.querySelector(".community-pulse-list");
  const fingerprint = `${state.lang}:${communityPulse.visibleFeedFingerprint(items)}`;
  list.hidden = items.length === 0;
  if (communityPulseListFingerprints.get(list) === fingerprint) return;

  const scrollTop = list.scrollTop;
  list.innerHTML = items.map(communityPulseItemMarkup).join("");
  communityPulseListFingerprints.set(list, fingerprint);
  list.scrollTop = scrollTop;
}

function communityPulseShellMarkup() {
  return `
    <section class="community-pulse" aria-labelledby="communityPulseTitle">
      <div class="community-pulse-heading">
        <div>
          <p class="eyebrow" data-pulse-copy="eyebrow">${copy("pulse_recent_reports")}</p>
          <h3 id="communityPulseTitle" data-pulse-copy="title">${copy("pulse_title")}</h3>
        </div>
        <div class="community-pulse-toggle" role="group" aria-label="${copy("pulse_area_aria")}">
          <button
            type="button"
            data-pulse-scope="all"
            aria-pressed="false"
          >${copy("pulse_all_recent")}</button>
          <button
            type="button"
            data-pulse-scope="county"
            aria-pressed="false"
          >${copy("pulse_in_your_area")}</button>
        </div>
      </div>
      <p class="community-pulse-status" aria-live="polite"></p>
      <ol class="community-pulse-list" aria-live="polite" hidden></ol>
    </section>
  `;
}

function communityPulseStatusText(itemCount) {
  if (state.communityPulseScope === "county" && !state.communityPulseCounty) {
    return copy("pulse_set_county");
  }
  if (state.communityPulseStatus === "loading" && itemCount === 0) return copy("pulse_loading");
  if (state.communityPulseStatus === "loading") return copy("pulse_refreshing");
  if (state.communityPulseStatus === "error") return copy("pulse_unavailable");
  if (state.communityPulseStatus === "stale") return copy("pulse_stale");
  if (itemCount === 0) {
    return state.communityPulseScope === "county"
      ? formatCopy("pulse_empty_county", { county: localizedCommunityPulseCounty() })
      : copy("pulse_empty_all");
  }
  return "";
}

function localizedCommunityPulseCounty() {
  const county = String(state.communityPulseCounty || "");
  return county.endsWith(" County")
    ? formatCopy("pulse_county_name", { county: county.slice(0, -" County".length) })
    : county;
}

function communityPulseItemMarkup(summary, index) {
  const row = communityPulse.formatRow(summary, formatCopy);
  const mobileRow = communityPulse.formatMobileRow(summary, formatCopy);
  const tooltipId = `communityPulseTooltip-${index}`;
  const tooltipClass = COMMUNITY_PULSE_TOOLTIPS_ENABLED ? " has-tooltip" : "";
  const tooltipExpanded = COMMUNITY_PULSE_TOOLTIPS_ENABLED ? 'aria-expanded="false"' : "";
  const tooltipDescription = COMMUNITY_PULSE_TOOLTIPS_ENABLED ? `aria-describedby="${tooltipId}"` : "";
  const tooltipMarkup = COMMUNITY_PULSE_TOOLTIPS_ENABLED
    ? `
      <div class="community-pulse-tooltip" id="${tooltipId}" role="tooltip">
        ${communityPulse.tooltipSections(summary, formatCopy).map(communityPulseTooltipSectionMarkup).join("")}
      </div>
    `
    : "";
  const parts = [
    `<span class="community-pulse-icons" aria-hidden="true">${escapeHtml(row.icons)}</span>`,
    `<span class="community-pulse-text">${escapeHtml(row.text)}</span>`,
    `<span class="community-pulse-time">${escapeHtml(row.time)}</span>`,
  ];
  if (row.own) parts.push(`<span class="community-pulse-own">${escapeHtml(row.own)}</span>`);
  return `
    <li class="community-pulse-item">
      <button
        type="button"
        class="community-pulse-row${tooltipClass}"
        data-pulse-row
        ${tooltipExpanded}
        ${tooltipDescription}
      >
        <span class="community-pulse-desktop-line">
          ${parts.join('<span class="community-pulse-separator" aria-hidden="true">·</span>')}
        </span>
        <span class="community-pulse-mobile-lines">
          <span class="community-pulse-mobile-top">
            <span class="community-pulse-mobile-icons" aria-hidden="true">${escapeHtml(mobileRow.icons)}</span>
            <span class="community-pulse-mobile-time">${escapeHtml(mobileRow.time)}</span>
            ${mobileRow.own ? `<span class="community-pulse-separator" aria-hidden="true">·</span><span class="community-pulse-own">${escapeHtml(mobileRow.own)}</span>` : ""}
          </span>
          <span class="community-pulse-mobile-bottom">
            <span class="community-pulse-mobile-summary">${escapeHtml(mobileRow.summary)}</span>
            <span class="community-pulse-separator" aria-hidden="true">·</span>
            <span class="community-pulse-mobile-location">${escapeHtml(mobileRow.location)}</span>
          </span>
        </span>
      </button>
      ${tooltipMarkup}
    </li>
  `;
}

function communityPulseTooltipSectionMarkup(section) {
  return `
    <div class="community-pulse-tooltip-section">
      ${section.title ? `<strong>${escapeHtml(section.title)}</strong>` : ""}
      ${section.lines.map((line) => `<span>${escapeHtml(line)}</span>`).join("")}
    </div>
  `;
}

function isCommunityPulseTap(event) {
  if (event.pointerType) return event.pointerType === "touch" || event.pointerType === "pen";
  return event.detail > 0 && window.matchMedia("(hover: none), (pointer: coarse)").matches;
}

function positionCommunityPulseTooltip(item, clientX, clientY) {
  const tooltip = item?.querySelector(".community-pulse-tooltip");
  if (!tooltip) return;
  const viewportPadding = 12;
  const cursorGap = 14;
  const maxTooltipWidth = Math.min(320, window.innerWidth - viewportPadding * 2);
  const left = Math.min(
    Math.max(viewportPadding, clientX + cursorGap),
    window.innerWidth - maxTooltipWidth - viewportPadding
  );
  const preferredTop = Math.max(viewportPadding, clientY + cursorGap);

  tooltip.style.setProperty("--community-pulse-tooltip-left", `${left}px`);
  tooltip.style.setProperty("--community-pulse-tooltip-top", `${preferredTop}px`);

  window.requestAnimationFrame(() => {
    const tooltipHeight = tooltip.getBoundingClientRect().height;
    const top = preferredTop + tooltipHeight > window.innerHeight - viewportPadding
      ? Math.max(viewportPadding, clientY - tooltipHeight - cursorGap)
      : preferredTop;
    tooltip.style.setProperty("--community-pulse-tooltip-top", `${top}px`);
  });
}


  export async function verifyBackendContract() {
    const response = await fetch(apiUrl("/openrpc.json"));
    if (response.status === 404) {
      throw new Error(`Backend not found at ${apiOrigin()}. Start the API there or use the local Caddy proxy.`);
    }
    if (!response.ok) {
      throw new Error(`OpenRPC endpoint returned HTTP ${response.status}`);
    }

    const openrpc = await response.json();
    const methods = openrpc.methods || [];
    const hasCommunityReportIngest = methods.some((method) => method.name === "ingest.community_reports");
    if (!hasCommunityReportIngest) {
      throw new Error(`Backend at ${apiOrigin()} does not expose ingest.community_reports.`);
    }
    const hasAttachmentUpload = methods.some((method) => method.name === "report_attachments.create_upload");
    if (!hasAttachmentUpload) {
      throw new Error(`Backend at ${apiOrigin()} does not expose report_attachments.create_upload.`);
    }
  }


  export function resetReport() {
    analytics.trackEvent("reset clicked", { screen: currentStep() });
    invalidateZipDetectionAttempt();
    cleanupPhotoLocationMap();
    clearUserSubmissionStatus();
    photoExifLocationResults.clear();
    photoProcessingStatusesByKey.clear();
    resetReportPhotoKeys();
    reportLocationInitializationGeneration += 1;
    reportLocationInteractionGeneration += 1;
    Object.assign(state, {
      step: 0,
      reportSubjects: [],
      accountSignedIn: state.accountSignedIn,
      selfReportUsesAccountPostalCode: false,
      peopleReports: {
        self: createPeopleReportState(),
        other: createPeopleReportState(),
      },
      categories: [],
      animalSpecies: "",
      animalType: "",
      animalGroup: "",
      animalStatus: "",
      animalSickCount: "",
      animalDeadCount: "",
      animalObservedDate: "",
      animalObservations: [],
      animalZip: "",
      animalNotes: "",
      animalPhotos: [],
      animalPhotoError: "",
      animalReporterFeeling: "",
      environmentIssueType: "",
      insectVectorType: "",
      environmentConcerns: [],
      environmentObservedDate: "",
      environmentObservations: [],
      environmentZip: "",
      environmentNotes: "",
      environmentPhotos: [],
      environmentPhotoError: "",
      environmentReporterFeeling: "",
      reportLocation: createReportLocationState(),
      zipDetectField: "",
      zipDetectMessage: "",
      zipDetectMessageKind: "",
      datePickerTarget: "",
      calendarMonth: "",
      travelAutocomplete: emptyLocationAutocompleteState(),
    });
    syncSelfReportPostalCodeFromAccount();
    resetEnvironmentReportState(state);
    clearOrdinarySubmissionAttempt();
    clearAccountReportDraft(localStorage);
    clearAccountReportSubmissionBatch(window.sessionStorage);
    reportFlowStarted = false;
    navigateToStep("frontDoor", { historyAPIMethod: "replaceState" });
  }


  export function renderSettings() {
    renderAccountActions();
    siteSettingsMenu.render();
  }


  export function renderAccountActions() {
    signInLink.textContent = copy("header_sign_in");
    registerLink.textContent = copy("header_register");
    dataMapsLink.textContent = copy("header_data_maps");
    const profileLabel = copy("header_profile");
    signedInLink.setAttribute("aria-label", profileLabel);
    signedInLink.setAttribute("title", profileLabel);
    signOutMenuItem.textContent = copy("header_sign_out");
    signInLink.hidden = state.accountSignedIn;
    registerLink.hidden = state.accountSignedIn;
    signedInLink.hidden = !state.accountSignedIn;
    signOutMenuItem.hidden = !state.accountSignedIn;
  }

  screen.addEventListener("click", (event) => {
    const target = event.target.closest("button");
    if (!target) return;

    if (target.matches([
      "[data-travel-location-suggestion]",
      "[data-action='select-date']",
      "[data-action='detect']",
      "[data-action='confirm-report-location']",
      "[data-action='remove-photo']",
      "[data-feeling]",
      "[data-category]",
      "[data-report-subject]",
      "[data-chip-group]",
    ].join(","))) {
      clearOrdinarySubmissionAttempt();
    }

    if (target.matches("[data-map-time-play-toggle]")) {
      toggleReportMapPlayback();
      return;
    }

    if (target.dataset.travelLocationSuggestion) {
      const index = Number.parseInt(target.dataset.travelLocationSuggestion, 10);
      const suggestion = state.travelAutocomplete.suggestions[index];
      const report = currentPeopleReport();
      if (suggestion) {
        report.exposures.recentTravelLocation = suggestion.label;
        report.exposures.recentTravelLocationSelection = suggestion;
        report.exposures.recentTravelLocationFallbackAllowed = false;
        state.travelAutocomplete = {
          ...emptyLocationAutocompleteState(suggestion.label),
          subject: peopleSubjectForStep(),
          status: "selected",
        };
        render();
      }
      return;
    }

    if (target.dataset.pulseScope) {
      state.communityPulseScope = target.dataset.pulseScope;
      state.communityPulseBackendItems = [];
      renderCommunityPulse();
      loadRecentCommunityReports({ deferIfBusy: true });
      return;
    }

    if (target.hasAttribute("data-pulse-row")) {
      if (!COMMUNITY_PULSE_TOOLTIPS_ENABLED) return;
      const item = target.closest(".community-pulse-item");
      if (!isCommunityPulseTap(event)) {
        if (item) positionCommunityPulseTooltip(item, event.clientX, event.clientY);
        return;
      }
      const isOpen = item?.classList.contains("open");
      document.querySelectorAll(".community-pulse-item.open").forEach((openItem) => {
        openItem.classList.remove("open");
        openItem.querySelector("[data-pulse-row]")?.setAttribute("aria-expanded", "false");
      });
      if (item && !isOpen) {
        item.classList.add("open");
        target.setAttribute("aria-expanded", "true");
        positionCommunityPulseTooltip(item, event.clientX, event.clientY);
      }
      return;
    }

    if (target.dataset.action === "map-lab-layer") {
      setMapLabCategoryVisible(target.dataset.category);
      return;
    }

    if (target.dataset.action === "map-lab-symptom-menu") {
      toggleMapLabSymptomMenu();
      return;
    }

    if (target.dataset.action === "zip-search") {
      void runZipSearch();
      return;
    }

    if (target.dataset.action === "open-date-picker") {
      openDatePicker(target.dataset.dateTarget || peopleDateTarget(peopleSubjectForStep()));
      return;
    }

    if (target.dataset.action === "close-date-picker") {
      closeDatePicker();
      render();
      return;
    }

    if (target.dataset.action === "move-date-picker") {
      const delta = Number.parseInt(target.dataset.monthDelta || "0", 10);
      moveDatePickerMonth(delta);
      render();
      return;
    }

    if (target.dataset.action === "select-date") {
      selectDatePickerDate(target.dataset.date || "");
      return;
    }

    if (target.dataset.feeling) {
      currentPeopleReport().feeling = target.dataset.feeling;
      render();
    }

    if (target.dataset.category) {
      const categoryWasSelected = state.categories.includes(target.dataset.category);
      const previousExactCategories = selectedReportLocationCategories().join("|");
      toggle(state.categories, target.dataset.category);
      if (!categoryWasSelected) {
        trackReportDomainSelected(target.dataset.category);
      }
      if (previousExactCategories !== selectedReportLocationCategories().join("|")) {
        state.reportLocation = createReportLocationState();
        reportLocationInitializationGeneration += 1;
        reportLocationInteractionGeneration += 1;
      }
      if (currentStep() === "frontDoor") {
        syncFrontDoorCategorySelection();
        return;
      }
      render();
    }

    if (target.dataset.reportSubject) {
      toggle(state.reportSubjects, target.dataset.reportSubject);
      render();
    }

    if (target.dataset.action === "register-and-continue") {
      clearUserSubmissionStatus();
      const resumePath = `/#${window.location.hash.replace(/^#/, "") || "/"}`;
      const registration = resumeAccountReportRegistration(localStorage, resumePath)
        || prepareAccountReportRegistration(localStorage, state);
      if (registration.ok) {
        window.location.assign(registration.destination);
      } else {
        setSubmissionStatus(
          copy("subject_signin_save_error"),
          `Account report draft storage failed: ${registration.error}`
        );
      }
    }

    if (target.dataset.action === "register-account") {
      window.location.assign(ACCOUNT_REGISTRATION_URL);
    }

    if (target.dataset.action === "skip-registration") {
      navigateToStep("done");
    }

    if (target.dataset.chipGroup) {
      const group = target.dataset.chipGroup;
      const value = target.dataset.chipValue;
      const report = currentPeopleReport();
      if (group === "symptom") toggle(report.symptoms, value);
      if (group === "observation") {
        const category = reportCategoryForStep();
        if (category) {
          const fields = observationFieldNames(category);
          toggle(state[fields.observations], value);
        }
      }
      if (group === "diagnosed") report.diagnosed = value;
      if (group === "diagnosis") report.diagnosis = value;
      if (group === "calledInSick") report.calledInSick = value;
      if (group.startsWith("exposure:")) setExposureAnswer(report, group.split(":")[1], value);
      if (group === "animalType") state.animalType = value;
      if (group === "animalGroup") {
        state.animalGroup = value;
        state.animalSpecies = "";
        state.animalType = "";
        state.animalStatus = "";
        state.animalSickCount = "";
        state.animalDeadCount = "";
      }
      if (group === "animalSpecies") {
        state.animalSpecies = value;
        state.animalType = animalTypeForSpecies(state.animalGroup, value);
      }
      if (group === "animalStatus") {
        state.animalStatus = value;
        if (value === "sick") state.animalDeadCount = "";
        if (value === "dead") state.animalSickCount = "";
      }
      if (group === "animalReporterFeeling") {
        state.animalReporterFeeling = value;
        if (value === "good" || value === "sick") {
          ensureSelected(state.reportSubjects, "self");
          state.peopleReports.self.feeling = value;
        } else {
          state.peopleReports.self.feeling = "";
        }
      }
      if (group === "environmentIssueType") {
        state.environmentIssueType = value;
        state.insectVectorType = "";
        state.environmentConcerns = [];
        state.environmentObservations = [];
      }
      if (group === "insectVectorType") {
        state.insectVectorType = value;
        state.environmentObservations = [];
      }
      if (group === "environmentConcern") {
        toggle(state.environmentConcerns, value);
        state.environmentObservations = [];
      }
      if (group === "environmentReporterFeeling") {
        state.environmentReporterFeeling = value;
        if (value === "good" || value === "sick") {
          ensureSelected(state.reportSubjects, "self");
          state.peopleReports.self.feeling = value;
        } else {
          state.peopleReports.self.feeling = "";
        }
      }
      render();
    }

    if (target.dataset.action === "detect") {
      detectZipForCurrentStep();
    }

    if (target.dataset.action === "confirm-report-location") {
      const nextStep = steps()[state.step + 1] || "done";
      reportLocationInteractionGeneration += 1;
      state.reportLocation = confirmReportLocation(state.reportLocation);
      if (isConfirmedPhotoReportLocation(state.reportLocation)) {
        navigateToStep(nextStep, { historyAPIMethod: "replaceState" });
        return;
      }
      render();
      return;
    }

    if (target.dataset.action === "photo") {
      const category = reportCategoryForStep();
      if (category === "animals" || category === "environment") {
        const fields = observationFieldNames(category);
        document.getElementById(fields.photoInputId)?.click();
      }
    }

    if (target.dataset.action === "remove-photo") {
      const category = target.dataset.photoCategory;
      const index = Number.parseInt(target.dataset.photoIndex || "", 10);
      if ((category === "animals" || category === "environment") && Number.isInteger(index)) {
        const fields = observationFieldNames(category);
        const removedKey = removeReportPhotoKey(category, index);
        const [removedFile] = state[fields.photos].splice(index, 1);
        state[fields.photoError] = "";
        if (removedFile || removedKey) handleReportPhotoRemoved(removedKey);
        render();
      }
    }

    if (target.dataset.action === "reset") resetReport();
  });

  screen.addEventListener("pointermove", (event) => {
    if (!COMMUNITY_PULSE_TOOLTIPS_ENABLED) return;
    const item = event.target.closest(".community-pulse-item");
    if (item) positionCommunityPulseTooltip(item, event.clientX, event.clientY);
  });

  screen.addEventListener("focusin", (event) => {
    if (!COMMUNITY_PULSE_TOOLTIPS_ENABLED) return;
    const row = event.target.closest("[data-pulse-row]");
    if (!row) return;
    const item = row.closest(".community-pulse-item");
    if (item) positionCommunityPulseTooltip(item, window.innerWidth / 2, window.innerHeight / 2);
  });

  screen.addEventListener("keydown", (event) => {
    if (event.key === "Enter" && event.target.matches("[data-zip-search-input]")) {
      event.preventDefault();
      void runZipSearch();
      return;
    }
    if (event.key !== "Escape" || !event.target.matches("[data-input='recentTravelLocation']")) return;
    resetTravelLocationAutocomplete();
    updateTravelLocationAutocompleteResults();
  });

  screen.addEventListener("paste", (event) => {
    const target = event.target;
    const field = target.dataset.input;
    if (!["peopleZip", "animalZip", "environmentZip"].includes(field) || !event.clipboardData) return;

    const start = target.selectionStart ?? target.value.length;
    const end = target.selectionEnd ?? start;
    const pastedValue = event.clipboardData.getData("text");
    const nextValue = `${target.value.slice(0, start)}${pastedValue}${target.value.slice(end)}`;
    const edit = sanitizeReportPostalCodeEdit(nextValue, start + pastedValue.length);
    event.preventDefault();
    target.value = edit.value;
    target.setSelectionRange(edit.selectionStart, edit.selectionEnd);
    target.dispatchEvent(new globalThis.InputEvent("input", {
      bubbles: true,
      data: pastedValue,
      inputType: "insertFromPaste",
    }));
  });

  screen.addEventListener("input", (event) => {
    const target = event.target;
    if (target.matches("[data-map-date-slider]")) {
      const selectedDate = sliderDateForIndex(state.mapTimeWindowStartDate, target.value);
      if (target.dataset.mapDateSlider === "start") {
        setReportMapStartDate(selectedDate);
      } else {
        setReportMapSelectedDate(selectedDate);
      }
      return;
    }

    if (!target.dataset.input) return;
    clearOrdinarySubmissionAttempt();
    const field = target.dataset.input;
    const report = currentPeopleReport();
    if (["peopleZip", "animalZip", "environmentZip"].includes(field)) {
      invalidateZipDetectionAttempt();
      const edit = sanitizeReportPostalCodeEdit(
        target.value,
        target.selectionStart,
        target.selectionEnd
      );
      if (edit.value !== target.value) {
        target.value = edit.value;
        target.setSelectionRange(edit.selectionStart, edit.selectionEnd);
      }
      setZipField(field, target.value);
      clearZipDetectMessage(field);
      updateZipInputValidity(field, target);
    }
    if (field === "sickCount") {
      const count = sanitizeCountInput(target.value, PEOPLE_SICK_COUNT_MAX_LENGTH);
      target.value = count;
      report.sickCount = count;
    }
    if (["animalSickCount", "animalDeadCount"].includes(field)) {
      const count = sanitizeCountInput(target.value, ANIMAL_COUNT_MAX_LENGTH);
      target.value = count;
      state[field] = count;
    }
    if (field === "animalSpecies") state.animalSpecies = target.value;
    if (["animalNotes", "environmentNotes"].includes(field)) state[field] = target.value;
    if (field === "otherSymptom") report.otherSymptom = target.value;
    if (field === "massGatheringEvent") report.exposures.massGatheringEvent = target.value;
    if (field === "recentTravelLocation") {
      report.exposures.recentTravelLocation = target.value;
      report.exposures.recentTravelLocationSelection = null;
      report.exposures.recentTravelLocationFallbackAllowed = false;
      void updateRecentTravelLocationAutocomplete(peopleSubjectForStep(), target.value);
    }
    nextButton.disabled = !canContinue();
  });

  screen.addEventListener("change", async (event) => {
    const target = event.target;
    if (target.dataset.action === "map-lab-symptom") {
      toggleMapLabSymptom(target.value, target.checked);
      return;
    }
    // "All symptoms" clears the individual checks; unchecking it is a no-op that setMapLabSymptoms
    // undoes when it re-syncs the box, so the menu always shows one state or the other.
    if (target.dataset.action === "map-lab-symptom-all") {
      setMapLabSymptoms([]);
      return;
    }
    if (!target.dataset.photoCategory || target.type !== "file") return;
    clearOrdinarySubmissionAttempt();

    const category = target.dataset.photoCategory;
    if (category !== "animals" && category !== "environment") return;

    const fields = observationFieldNames(category);
    const nextFiles = Array.from(target.files || []);
    const error = validatePhotoSelection(state[fields.photos], nextFiles);
    let nextSelections = [];

    if (!error) {
      const nextKeys = appendReportPhotoKeys(category, nextFiles.length);
      nextSelections = nextFiles.map((file, index) => ({
        file,
        key: nextKeys[index],
      }));
      state[fields.photos] = [...state[fields.photos], ...nextFiles];
      state[fields.photoError] = "";
    } else {
      state[fields.photoError] = error;
    }

    target.value = "";
    render();

    if (!error && nextSelections.length) {
      await updatePhotoExifLocationStatuses(nextSelections);
      render();
    }
  });

  nextButton.addEventListener("click", async () => {
    if (!canContinue()) return;
    invalidateZipDetectionAttempt();
    normalizeCurrentStepPostalCode();
    if (isSubmitStep()) await submitReport();
    else {
      navigateToStep(steps()[state.step + 1] || "done");
    }
  });

  backButton.addEventListener("click", () => {
    if (state.step > 0) {
      navigateToStep(steps()[state.step - 1] || "frontDoor");
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape" || !state.datePickerTarget) return;
    closeDatePicker();
    render();
  });

  signOutMenuItem.addEventListener("click", async () => {
    siteSettingsMenu.setMenuOpen(false);
    try {
      const response = await fetch(apiUrl("/account/logout"), {
        method: "POST",
        credentials: "same-origin",
      });
      if (!response.ok) throw new Error("logout failed");
      accountSessionRequestGuard.invalidate();
      applyAccountSession({ authenticated: false, email: "", postalCode: "" });
      clearUserSubmissionStatus();
      setSubmissionStatus(copy("account_signed_out"));
      refreshCurrentRouteAfterAccountSession();
    } catch {
      setSubmissionStatus(copy("account_sign_out_error"));
    }
  });

  // The symptom checkbox menu stays open across checks, so an outside click closes it.
  document.addEventListener("click", (event) => {
    if (event.target.closest(".map-lab-symptom-menu")) return;
    setMapLabSymptomMenuOpen(false);
  });

  debugTrigger.hidden = !DEBUG_MODE;
  debugTrigger.addEventListener("click", () => setDebugPanelOpen(true));
  document.getElementById("closeDebug").addEventListener("click", () => setDebugPanelOpen(false));
  debugBackdrop.addEventListener("click", () => setDebugPanelOpen(false));
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") setMapLabSymptomMenuOpen(false);
    if (event.key === "Escape" && !debugPanel.hidden) setDebugPanelOpen(false);
    if (event.key === "Escape") {
      document.querySelectorAll(".community-pulse-item.open").forEach((item) => {
        item.classList.remove("open");
        item.querySelector("[data-pulse-row]")?.setAttribute("aria-expanded", "false");
      });
    }
  });


  export function toggle(list, value) {
    const index = list.indexOf(value);
    if (index === -1) list.push(value);
    else list.splice(index, 1);
  }

  export function applyAccountSession(accountSession) {
    if (state.selfReportUsesAccountPostalCode) {
      state.peopleReports.self.zip = "";
    }
    state.selfReportUsesAccountPostalCode = false;
    state.accountSignedIn = accountSession.authenticated;
    state.accountEmail = accountSession.email;
    state.accountPostalCode = accountSession.postalCode;
    renderAccountActions();
    return state.accountSignedIn;
  }


  export function syncSelfReportPostalCodeFromAccount() {
    const next = selfReportPostalCodeForAccountSession({
      authenticated: state.accountSignedIn,
      postalCode: state.accountPostalCode,
      currentPostalCode: state.peopleReports.self.zip,
      usesAccountPostalCode: state.selfReportUsesAccountPostalCode,
    });
    state.peopleReports.self.zip = next.postalCode;
    state.selfReportUsesAccountPostalCode = next.usesAccountPostalCode;
  }


export async function reconcileAccountSession(accountSession) {
    await reconcileAccountSessionFlow({
      session: accountSession,
      applySession: applyAccountSession,
      restorePendingReport: restorePendingAccountReport,
      enrichSelfReport: syncSelfReportPostalCodeFromAccount,
      refreshRoute: () => {
        accountSessionResolvedForAnalytics = true;
        refreshCurrentRouteAfterAccountSession();
      },
    });
    return state.accountSignedIn;
  }


  export async function loadAccountSession() {
    const requestIsCurrent = accountSessionRequestGuard.begin();
    return accountSessionDetails(
      fetch,
      apiUrl("/account/me"),
      ACCOUNT_SESSION_TIMEOUT_MS,
      reconcileAccountSession,
      requestIsCurrent
    );
  }


  export async function restorePendingAccountReport() {
    const requestedPath = window.location.hash.replace(/^#/, "") || "/";
    const requestedRoute = normalizeRoute(requestedPath);
    if (!state.accountSignedIn || requestedRoute !== ANOTHER_PERSON_RESUME_PATH.slice(2)) return false;
    const draft = await loadAccountReportDraftForAuthenticatedResume(
      localStorage,
      `/#${requestedPath}`,
      state.accountEmail
    );
    if (!draft) return false;
    state.categories = [...draft.categories];
    state.reportSubjects = [...draft.report_subjects];
    state.peopleReports = {
      self: draft.people_reports.self,
      other: draft.people_reports.other,
    };
    state.accountReportSubmissionBatchId = null;
    return true;
  }


  export function refreshCurrentRouteAfterAccountSession() {
    const requestedRoute = normalizeRoute(window.location.hash.replace(/^#/, "") || "/");
    activateRoute(stepForRoute(requestedRoute) || "frontDoor");
  }


  export function initializeCommunityReportApp() {
    prepareAccountReportDraftForAppStart(localStorage);
    analytics.initAnalytics();
    return initializeReportingBeforeAccountCheck({
      initializeReporting: () => {
        initializeRouter();
        loadCommunityReportCount();
        loadRecentCommunityReports();
        window.setInterval(() => {
          if (!document.hidden && document.querySelector(".community-pulse-slot")) {
            loadRecentCommunityReports();
          }
        }, COMMUNITY_PULSE_POLL_MS);
      },
      resolveAccountSession: loadAccountSession,
      accountSessionResolved: reconcileAccountSession,
    });
  }


  initializeCommunityReportApp();
